import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Container, Stack, Box } from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Button } from "src/@/components/ui/button";
import { Input } from "src/@/components/ui/input";
import { Textarea } from "src/@/components/ui/textarea";
import { Card, CardContent } from "src/@/components/ui/card";
import { Switch } from "src/@/components/ui/switch";
import { Label } from "src/@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "src/@/components/ui/select";
import { Separator } from "src/@/components/ui/separator";
import {
    Dialog,
    DialogContent
} from "src/@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "src/@/components/ui/radio-group";
import { Checkbox } from "src/@/components/ui/checkbox";
import { useToast } from "src/@/components/ui/use-toast";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/@/components/ui/table";
import { Badge } from "src/@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/@/components/ui/sheet";
import { ScrollArea } from "src/@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "src/@/components/ui/popover";
import formulariosService from 'src/services/formulariosService';
import useAuth from 'src/hooks/useAuth';
import useSettings from 'src/hooks/useSettings';
import config from 'src/config';
import { verificarUrlProvider } from 'src/utils/functions';

// Utils
const generateId = () => Math.random().toString(36).substr(2, 9);

const QUESTION_TYPES = [
    { value: 'short_text', label: 'Resposta Curta', icon: 'mdi:format-text' },
    { value: 'paragraph', label: 'Parágrafo', icon: 'mdi:text-long' },
    { value: 'multiple_choice', label: 'Múltipla Escolha', icon: 'mdi:radiobox-marked' },
    { value: 'checkbox', label: 'Caixas de Seleção', icon: 'mdi:checkbox-marked' },
    { value: 'dropdown', label: 'Lista Suspensa', icon: 'mdi:arrow-down-drop-circle' },
    { value: 'info_block', label: 'Bloco de Informações', icon: 'mdi:card-text-outline' },
    { value: 'file_upload', label: 'Upload de Arquivo', icon: 'mdi:file-upload-outline' },
];

const SHORT_TEXT_INPUT_TYPES = [
    { value: 'text', label: 'Texto livre', icon: 'mdi:format-text', placeholder: 'Digite sua resposta...', htmlType: 'text' },
    { value: 'email', label: 'E-mail', icon: 'mdi:email-outline', placeholder: 'seu@email.com', htmlType: 'email' },
    { value: 'cpf', label: 'CPF', icon: 'mdi:card-account-details-outline', placeholder: '000.000.000-00', htmlType: 'text' },
    { value: 'cnpj', label: 'CNPJ', icon: 'mdi:domain', placeholder: '00.000.000/0000-00', htmlType: 'text' },
    { value: 'phone', label: 'Telefone', icon: 'mdi:phone-outline', placeholder: '(00) 00000-0000', htmlType: 'tel' },
    { value: 'number', label: 'Numérico', icon: 'mdi:numeric', placeholder: '0', htmlType: 'number' },
    { value: 'date', label: 'Data', icon: 'mdi:calendar', placeholder: 'dd/mm/aaaa', htmlType: 'date' },
    { value: 'url', label: 'URL / Link', icon: 'mdi:link-variant', placeholder: 'https://...', htmlType: 'url' },
];

const BLOCK_FIELD_TYPES = [
    { value: 'text', label: 'Texto' },
    { value: 'date', label: 'Data' },
    { value: 'textarea', label: 'Texto longo' },
];

export default function FormBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { toast } = useToast();
    const { user } = useAuth();
    const { themeMode } = useSettings();
    const isDark = themeMode === 'dark';

    // Estados do formulário
    const [formId, setFormId] = useState(id);
    const [formStatus, setFormStatus] = useState('draft');
    const [title, setTitle] = useState('Novo Formulário');
    const [description, setDescription] = useState('');
    const [questions, setQuestions] = useState([]);
    const [activeQuestion, setActiveQuestion] = useState(null);
    const [isStepByStep, setIsStepByStep] = useState(false);
    const [isPublished, setIsPublished] = useState(false);
    const [publicId, setPublicId] = useState(null);
    const [settings, setSettings] = useState({
        allowMultipleResponses: true,
        requireIdentification: false,
        backgroundColor: '#7C3AED',
        logoUrl: null,
        identificationFields: {
            name: { enabled: false, required: false },
            email: { enabled: false, required: false },
            phone: { enabled: false, required: false },
            cpf: { enabled: false, required: false },
        }
    });
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [activeTab, setActiveTab] = useState(() => {
        const tabParam = searchParams.get('tab');
        return ['editor', 'configurações', 'respostas'].includes(tabParam) ? tabParam : 'editor';
    });
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewStep, setPreviewStep] = useState(0);
    const [previewBlockCounts, setPreviewBlockCounts] = useState({});

    const getPreviewBlockCount = (qId) => previewBlockCounts[qId] || 1;
    const handlePreviewAddBlock = (qId, max) => {
        setPreviewBlockCounts(prev => {
            const current = prev[qId] || 1;
            if (current >= max) return prev;
            return { ...prev, [qId]: current + 1 };
        });
    };
    const handlePreviewRemoveBlock = (qId) => {
        setPreviewBlockCounts(prev => {
            const current = prev[qId] || 1;
            if (current <= 1) return prev;
            return { ...prev, [qId]: current - 1 };
        });
    };

    // Estados de loading
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Estados de respostas
    const [respostas, setRespostas] = useState([]);
    const [respostasPagination, setRespostasPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
    const [estatisticas, setEstatisticas] = useState(null);
    const [loadingRespostas, setLoadingRespostas] = useState(false);
    const [respostaDetalhe, setRespostaDetalhe] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [filtroStatus, setFiltroStatus] = useState('all');
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');
    const [respostasViewMode, setRespostasViewMode] = useState('resumo'); // 'resumo' | 'planilha'
    const [planilhaFilters, setPlanilhaFilters] = useState({}); // { columnKey: Set of selected values }
    const [planilhaFilterOpen, setPlanilhaFilterOpen] = useState(null); // columnKey that is open
    const [planilhaFilterSearch, setPlanilhaFilterSearch] = useState(''); // search within filter dropdown
    const [planilhaFilterOptions, setPlanilhaFilterOptions] = useState({}); // { columnKey: [values] } from backend
    const [loadingFilterOptions, setLoadingFilterOptions] = useState(null); // columnKey currently loading
    const planilhaFilterRef = React.useRef(null);

    // Redirecionar se não houver ID (deve sempre ter, pois o draft é criado na listagem)
    useEffect(() => {
        if (!id) {
            navigate('/dashboard/formularios');
            return;
        }
        loadFormulario();
    }, [id, navigate]);

    // Set active question on load
    useEffect(() => {
        if (questions.length > 0 && !activeQuestion) {
            setActiveQuestion(questions[0].id);
        }
    }, [questions, activeQuestion]);

    const loadFormulario = async () => {
        try {
            setLoading(true);
            const response = await formulariosService.buscar(id);
            if (!response.error && response.data) {
                const form = response.data;
                setTitle(form.title || 'Novo Formulário');
                setDescription(form.description || '');
                setFormStatus(form.status || 'draft');
                // Se não houver perguntas, adiciona uma inicial
                const loadedQuestions = form.questions?.length > 0
                    ? form.questions
                    : [{ id: generateId(), title: '', type: 'short_text', required: false, options: ['Opção 1'] }];
                setQuestions(loadedQuestions);
                setIsStepByStep(form.isStepByStep || false);
                setIsPublished(form.isPublished || false);
                setPublicId(form.publicId || null);
                setFormId(form._id);
                if (form.settings) {
                    setSettings(prev => ({
                        ...prev,
                        ...form.settings,
                        identificationFields: {
                            ...prev.identificationFields,
                            ...(form.settings.identificationFields || {})
                        }
                    }));
                }
            } else {
                // Formulário não encontrado, redireciona
                toast({
                    title: "Erro",
                    description: "Formulário não encontrado",
                    variant: "destructive"
                });
                navigate('/dashboard/formularios');
            }
        } catch (error) {
            console.error('Erro ao carregar formulário:', error);
            toast({
                title: "Erro",
                description: "Não foi possível carregar o formulário",
                variant: "destructive"
            });
            navigate('/dashboard/formularios');
        } finally {
            setLoading(false);
        }
    };

    // Carregar respostas quando tab muda para 'respostas'
    useEffect(() => {
        if (activeTab === 'respostas' && formId) {
            loadRespostas();
            loadEstatisticas();
        }
    }, [activeTab, formId]);

    // Recarregar quando filtro de status muda
    useEffect(() => {
        if (activeTab === 'respostas' && formId) {
            setPlanilhaFilterOptions({}); // invalidar cache de opções
            loadRespostas(1);
        }
    }, [filtroStatus]);

    // Recarregar quando filtros da planilha mudam
    useEffect(() => {
        if (activeTab === 'respostas' && formId) {
            loadRespostas(1);
        }
    }, [planilhaFilters]);

    // Recarregar quando filtro de data muda
    useEffect(() => {
        if (activeTab === 'respostas' && formId) {
            setPlanilhaFilterOptions({});
            loadRespostas(1);
        }
    }, [filtroDataInicio, filtroDataFim]);

    const loadRespostas = async (page = 1, filtersOverride) => {
        try {
            setLoadingRespostas(true);
            const params = { page, limit: respostasPagination.limit };
            if (filtroStatus !== 'all') params.status = filtroStatus;
            if (filtroDataInicio) params.startDate = filtroDataInicio;
            if (filtroDataFim) params.endDate = new Date(filtroDataFim + 'T23:59:59').toISOString();

            // Enviar filtros da planilha ao backend
            const activeFilters = filtersOverride !== undefined ? filtersOverride : planilhaFilters;
            const filtersForApi = {};
            for (const [colKey, valSet] of Object.entries(activeFilters)) {
                if (valSet && valSet.size > 0) {
                    filtersForApi[colKey] = [...valSet];
                }
            }
            if (Object.keys(filtersForApi).length > 0) {
                params.filters = JSON.stringify(filtersForApi);
            }

            const response = await formulariosService.listarRespostas(formId, params);
            if (!response.error) {
                setRespostas(response.data || []);
                if (response.pagination) setRespostasPagination(response.pagination);
            }
        } catch (error) {
            console.error('Erro ao carregar respostas:', error);
        } finally {
            setLoadingRespostas(false);
        }
    };

    const loadEstatisticas = async () => {
        try {
            const response = await formulariosService.estatisticas(formId);
            if (!response.error) {
                setEstatisticas(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar estatísticas:', error);
        }
    };

    const handleVerResposta = async (respostaId) => {
        try {
            const response = await formulariosService.buscarResposta(respostaId);
            if (!response.error) {
                setRespostaDetalhe(response.data);
                setSheetOpen(true);
            }
        } catch (error) {
            console.error('Erro ao carregar resposta:', error);
            toast({ title: "Erro", description: "Não foi possível carregar a resposta", variant: "destructive" });
        }
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '-';
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const formatDateOnly = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const statusConfig = {
        completed: { label: 'Completa', className: 'bg-green-100 text-green-700 border-green-200' },
        started: { label: 'Iniciada', className: 'bg-blue-100 text-blue-700 border-blue-200' },
        abandoned: { label: 'Abandonada', className: 'bg-red-100 text-red-700 border-red-200' },
    };

    // Helper: extrair valor de resposta para uma pergunta
    const getAnswerForQuestion = (resposta, questionId) => {
        const answer = resposta.answers?.find(a => a.question_id === questionId);
        if (!answer) return '';
        if (Array.isArray(answer.value)) {
            // file_upload: array de objetos com url/filename
            if (answer.question_type === 'file_upload' && answer.value.length > 0 && typeof answer.value[0] === 'object' && answer.value[0].url) {
                return answer.value.map(f => f.filename || f.url).join(', ');
            }
            // info_block: array de objetos com campos (chaves são field.id)
            if (answer.value.length > 0 && typeof answer.value[0] === 'object') {
                const question = questions.find(q => q.id === questionId);
                const fieldMap = {};
                (question?.blockFields || []).forEach(f => { fieldMap[f.id] = f.label || f.id; });
                return answer.value.map((block) =>
                    Object.entries(block).map(([k, v]) => `${fieldMap[k] || k}: ${v}`).join(', ')
                ).join(' | ');
            }
            return answer.value.join(', ');
        }
        return answer.value || '';
    };

    // Extrair valor de coluna de uma resposta
    // forFilter: quando true, agrupa datas por dia (sem horário)
    const getColumnValue = (resposta, colKey, forFilter = false) => {
        if (colKey === '_respondente') return resposta.respondent?.name || 'Anônimo';
        if (colKey === '_status') return statusConfig[resposta.status]?.label || resposta.status || '';
        if (colKey === '_data') return forFilter ? formatDateOnly(resposta.created_at) : formatDate(resposta.created_at);
        // Para perguntas do tipo date, agrupar por dia no filtro
        if (forFilter) {
            const question = questions.find(q => q.id === colKey);
            if (question && question.type === 'date') {
                const val = getAnswerForQuestion(resposta, colKey);
                if (val) return formatDateOnly(val);
                return '(vazio)';
            }
        }
        return getAnswerForQuestion(resposta, colKey) || '(vazio)';
    };

    // Buscar valores únicos de uma coluna do backend
    const loadFilterOptions = async (colKey) => {
        if (planilhaFilterOptions[colKey]) return; // já carregou
        try {
            setLoadingFilterOptions(colKey);
            const params = { columns: colKey };
            if (filtroStatus !== 'all') params.status = filtroStatus;
            const response = await formulariosService.filtrosRespostas(formId, params);
            if (!response.error && response.data) {
                setPlanilhaFilterOptions(prev => ({ ...prev, ...response.data }));
            }
        } catch (error) {
            console.error('Erro ao carregar opções de filtro:', error);
        } finally {
            setLoadingFilterOptions(null);
        }
    };

    // Filtragem agora é feita no backend — usar respostas diretamente
    const filteredRespostas = respostas;

    // Fechar dropdown ao clicar fora
    React.useEffect(() => {
        if (!planilhaFilterOpen) return;
        const handleClick = (e) => {
            if (planilhaFilterRef.current && !planilhaFilterRef.current.contains(e.target)) {
                setPlanilhaFilterOpen(null);
                setPlanilhaFilterSearch('');
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [planilhaFilterOpen]);

    // Toggle um valor no filtro de uma coluna
    const toggleFilterValue = (colKey, value) => {
        setPlanilhaFilters(prev => {
            const current = prev[colKey] ? new Set(prev[colKey]) : new Set();
            if (current.has(value)) current.delete(value);
            else current.add(value);
            if (current.size === 0) { const next = { ...prev }; delete next[colKey]; return next; }
            return { ...prev, [colKey]: current };
        });
    };

    // Selecionar/deselecionar todos no filtro
    const setFilterAll = (colKey, values, selectAll) => {
        setPlanilhaFilters(prev => {
            const next = { ...prev };
            if (selectAll || values.length === 0) { delete next[colKey]; }
            else { next[colKey] = new Set(values); }
            return next;
        });
    };

    const hasActiveFilters = Object.values(planilhaFilters).some(s => s && s.size > 0);
    const activeFilterCount = Object.values(planilhaFilters).filter(s => s && s.size > 0).length;

    // Exportar CSV
    const handleExportCSV = () => {
        if (respostas.length === 0) return;
        const sep = ';';
        const headers = ['Respondente', 'Email', 'Telefone', 'Status', 'Data', ...questions.map(q => q.title || 'Pergunta')];
        const escapeCSV = (val) => {
            const str = String(val ?? '');
            if (str.includes(sep) || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
            return str;
        };
        const rows = respostas.map(r => [
            r.respondent?.name || 'Anônimo',
            r.respondent?.email || '',
            r.respondent?.phone || '',
            statusConfig[r.status]?.label || r.status,
            formatDate(r.created_at),
            ...questions.map(q => getAnswerForQuestion(r, q.id))
        ]);
        const csv = [headers.map(escapeCSV).join(sep), ...rows.map(row => row.map(escapeCSV).join(sep))].join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `respostas-${title || 'formulario'}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Salvar formulário (sempre edição, pois o draft já foi criado)
    const handleSave = useCallback(async () => {
        if (!formId) {
            toast({
                title: "Erro",
                description: "ID do formulário não encontrado",
                variant: "destructive"
            });
            return;
        }

        try {
            setSaving(true);
            const data = {
                title,
                description,
                questions,
                isStepByStep,
                settings,
                userId: user?.id
            };

            const response = await formulariosService.editar(formId, data);

            if (!response.error) {
                setHasChanges(false);
                if (response.data?.status) {
                    setFormStatus(response.data.status);
                }
                toast({
                    title: "Salvo!",
                    description: "Formulário salvo com sucesso",
                });
            } else {
                toast({
                    title: "Erro",
                    description: response.message || "Não foi possível salvar o formulário",
                    variant: "destructive"
                });
            }
        } catch (error) {
            console.error('Erro ao salvar:', error);
            toast({
                title: "Erro",
                description: "Não foi possível salvar o formulário",
                variant: "destructive"
            });
        } finally {
            setSaving(false);
        }
    }, [formId, title, description, questions, isStepByStep, settings, user, toast]);

    // Publicar formulário
    const handlePublish = async () => {
        if (!formId) {
            toast({
                title: "Erro",
                description: "ID do formulário não encontrado",
                variant: "destructive"
            });
            return;
        }

        try {
            // Salvar primeiro se houver mudanças
            if (hasChanges) {
                await handleSave();
            }

            const newPublishState = !isPublished;
            const response = await formulariosService.togglePublicar(formId, newPublishState);

            if (!response.error) {
                setIsPublished(newPublishState);
                if (response.data?.publicId) {
                    setPublicId(response.data.publicId);
                }
                // Ao publicar, o status muda para 'active'
                if (newPublishState && response.data?.status) {
                    setFormStatus(response.data.status);
                }
                toast({
                    title: newPublishState ? "Publicado!" : "Despublicado",
                    description: newPublishState
                        ? "Seu formulário está disponível para respostas"
                        : "Seu formulário foi despublicado",
                });
            }
        } catch (error) {
            console.error('Erro ao publicar:', error);
            toast({
                title: "Erro",
                description: "Não foi possível alterar o status de publicação",
                variant: "destructive"
            });
        }
    };

    // Marcar como tendo mudanças
    const markAsChanged = () => {
        if (!hasChanges) setHasChanges(true);
    };

    const handleAddQuestion = () => {
        const newQuestion = {
            id: generateId(),
            title: '',
            type: 'short_text',
            required: false,
            options: ['Opção 1']
        };
        setQuestions([...questions, newQuestion]);
        setActiveQuestion(newQuestion.id);
        markAsChanged();
    };

    const handleUpdateQuestion = (id, field, value) => {
        setQuestions(questions.map(q => {
            if (q.id !== id) return q;
            const updated = { ...q, [field]: value };
            // Inicializar campos padrão ao trocar para info_block
            if (field === 'type' && value === 'info_block' && !q.blockFields?.length) {
                updated.blockFields = [
                    { id: generateId(), label: 'Campo 1', type: 'text' },
                ];
                updated.blockRepeat = 3;
            }
            // Inicializar config padrão ao trocar para file_upload
            if (field === 'type' && value === 'file_upload' && !q.fileUploadConfig) {
                updated.fileUploadConfig = {
                    maxFiles: 1,
                    maxFileSize: 10,
                    acceptedTypes: [],
                };
            }
            return updated;
        }));
        markAsChanged();
    };

    const handleAddOption = (questionId) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                return { ...q, options: [...(q.options || []), `Opção ${(q.options?.length || 0) + 1}`] };
            }
            return q;
        }));
        markAsChanged();
    };

    const handleOptionChange = (questionId, index, value) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                const newOptions = [...q.options];
                newOptions[index] = value;
                return { ...q, options: newOptions };
            }
            return q;
        }));
        markAsChanged();
    };

    const handleRemoveOption = (questionId, index) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                const newOptions = q.options.filter((_, i) => i !== index);
                return { ...q, options: newOptions };
            }
            return q;
        }));
        markAsChanged();
    };

    // --- Handlers para info_block ---
    const handleAddBlockField = (questionId) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                const fields = q.blockFields || [];
                return {
                    ...q,
                    blockFields: [...fields, { id: generateId(), label: `Campo ${fields.length + 1}`, type: 'text' }]
                };
            }
            return q;
        }));
        markAsChanged();
    };

    const handleUpdateBlockField = (questionId, fieldId, key, value) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                return {
                    ...q,
                    blockFields: (q.blockFields || []).map(f =>
                        f.id === fieldId ? { ...f, [key]: value } : f
                    )
                };
            }
            return q;
        }));
        markAsChanged();
    };

    const handleRemoveBlockField = (questionId, fieldId) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                return {
                    ...q,
                    blockFields: (q.blockFields || []).filter(f => f.id !== fieldId)
                };
            }
            return q;
        }));
        markAsChanged();
    };

    const handleDeleteQuestion = (id) => {
        const newQuestions = questions.filter(q => q.id !== id);
        setQuestions(newQuestions);
        if (activeQuestion === id && newQuestions.length > 0) {
            setActiveQuestion(newQuestions[newQuestions.length - 1].id);
        }
        markAsChanged();
    };

    const handleDuplicateQuestion = (question) => {
        const newQuestion = {
            ...question,
            id: generateId(),
            ...(question.blockFields ? {
                blockFields: question.blockFields.map(f => ({ ...f, id: generateId() }))
            } : {})
        };
        const index = questions.findIndex(q => q.id === question.id);
        const newQuestions = [...questions];
        newQuestions.splice(index + 1, 0, newQuestion);
        setQuestions(newQuestions);
        setActiveQuestion(newQuestion.id);
        markAsChanged();
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(questions);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setQuestions(items);
        markAsChanged();
    };

    const renderQuestionInput = (question) => {
        switch (question.type) {
            case 'short_text': {
                const inputCfg = SHORT_TEXT_INPUT_TYPES.find(t => t.value === question.inputType) || SHORT_TEXT_INPUT_TYPES[0];
                return (
                    <div className="pt-2 flex items-center gap-2">
                        <Iconify icon={inputCfg.icon} className="text-blue-400/70" width={16} />
                        <input
                            disabled
                            className="w-1/2 bg-transparent text-sm text-blue-400/70 border-b border-blue-400 outline-none py-1"
                            placeholder={inputCfg.placeholder}
                        />
                        {question.inputType && question.inputType !== 'text' && (
                            <span className="text-[10px] bg-violet-50 text-violet-600 px-1.5 py-0.5 rounded font-medium">
                                {inputCfg.label}
                            </span>
                        )}
                    </div>
                );
            }
            case 'paragraph':
                return (
                    <div className="border-b border-gray-200 py-2">
                        <input disabled className="w-full bg-transparent text-sm text-gray-400" placeholder="Sua resposta..." />
                    </div>
                );
            case 'multiple_choice':
            case 'checkbox':
            case 'dropdown':
                return (
                    <div className="space-y-2 mt-4">
                        {question.options?.map((option, index) => (
                            <div key={index} className="flex items-center gap-2 group">
                                <Iconify
                                    icon={question.type === 'multiple_choice' ? 'mdi:radiobox-blank' : question.type === 'checkbox' ? 'mdi:checkbox-blank-outline' : 'mdi:numeric'}
                                    className="text-slate-400"
                                />
                                <Input
                                    value={option}
                                    onChange={(e) => handleOptionChange(question.id, index, e.target.value)}
                                    className="h-8 border-transparent hover:border-slate-200 focus:border-violet-500 transition-all"
                                    placeholder={`Opção ${index + 1}`}
                                />
                                {question.options.length > 1 && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemoveOption(question.id, index)}
                                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <Iconify icon="mdi:close" className="text-slate-400 hover:text-red-500" />
                                    </Button>
                                )}
                            </div>
                        ))}
                        <div className="flex items-center gap-2 pt-1">
                            <Iconify
                                icon={question.type === 'multiple_choice' ? 'mdi:radiobox-blank' : question.type === 'checkbox' ? 'mdi:checkbox-blank-outline' : 'mdi:numeric'}
                                className="text-slate-400 opacity-50"
                            />
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAddOption(question.id)}
                                className="text-slate-500 hover:text-violet-600 h-8 font-normal"
                            >
                                Adicionar opção
                            </Button>
                        </div>
                    </div>
                );
            case 'info_block':
                return (
                    <div className="space-y-3 mt-2">
                        <div className="border border-gray-200 dark:border-[#383838] rounded-lg p-4 space-y-3 bg-gray-50/50 dark:bg-[#1A1D21]/50">
                            <p className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider">
                                {question.title || 'Bloco'} #1
                            </p>
                            {(question.blockFields || []).map((field) => (
                                <div key={field.id} className="space-y-1">
                                    <label className="text-xs text-gray-500 dark:text-[#ABABAD] font-medium">{field.label}</label>
                                    {field.type === 'textarea' ? (
                                        <div className="w-full h-16 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-md px-3 py-2 text-sm text-gray-300">
                                            {field.label}...
                                        </div>
                                    ) : field.type === 'date' ? (
                                        <div className="w-48 h-9 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-md px-3 flex items-center text-sm text-gray-300 gap-2">
                                            <Iconify icon="mdi:calendar" width={14} className="text-gray-300" />
                                            dd/mm/aaaa
                                        </div>
                                    ) : (
                                        <div className="w-full h-9 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-md px-3 flex items-center text-sm text-gray-300">
                                            {field.label}...
                                        </div>
                                    )}
                                </div>
                            ))}
                            {(!question.blockFields || question.blockFields.length === 0) && (
                                <p className="text-xs text-gray-400 italic py-2">Nenhum campo configurado</p>
                            )}
                        </div>
                        {(question.blockRepeat || 3) > 1 && (
                            <div className="flex items-center justify-center gap-1.5 py-2.5 border border-dashed border-gray-300 dark:border-[#383838] rounded-lg text-xs font-medium text-gray-400 dark:text-[#ABABAD]">
                                <Iconify icon="mdi:plus-circle-outline" width={16} />
                                Adicionar {question.title || 'bloco'} (1/{question.blockRepeat || 3})
                            </div>
                        )}
                    </div>
                );
            case 'file_upload': {
                const cfg = question.fileUploadConfig || {};
                const acceptLabel = (cfg.acceptedTypes || []).length > 0
                    ? cfg.acceptedTypes.map(t => t.endsWith('/*') ? t.replace('/*', '') : t).join(', ')
                    : 'Todos os tipos';
                return (
                    <div className="mt-3 border-2 border-dashed border-gray-200 dark:border-[#383838] rounded-xl p-6 flex flex-col items-center gap-2 bg-gray-50/50 dark:bg-[#1A1D21]/50">
                        <div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                            <Iconify icon="mdi:cloud-upload-outline" width={22} className="text-violet-500" />
                        </div>
                        <p className="text-sm font-medium text-gray-500 dark:text-[#ABABAD]">Arraste ou clique para enviar</p>
                        <p className="text-[10px] text-gray-400">
                            {acceptLabel} · Máx {cfg.maxFileSize || 10}MB · {cfg.maxFiles || 1} arquivo(s)
                        </p>
                    </div>
                );
            }
            default:
                return null;
        }
    };

    const currentQuestion = questions.find(q => q.id === activeQuestion) || questions[0];

    // Preview: campos de identificação habilitados
    const enabledIdentificationFields = settings.requireIdentification
        ? [
            settings.identificationFields.name?.enabled && { key: 'name', label: 'Nome', icon: 'mdi:account-outline', placeholder: 'Seu nome completo', type: 'text', required: settings.identificationFields.name?.required },
            settings.identificationFields.email?.enabled && { key: 'email', label: 'E-mail', icon: 'mdi:email-outline', placeholder: 'seu@email.com', type: 'email', required: settings.identificationFields.email?.required },
            settings.identificationFields.phone?.enabled && { key: 'phone', label: 'Telefone', icon: 'mdi:phone-outline', placeholder: '(00) 00000-0000', type: 'tel', required: settings.identificationFields.phone?.required },
            settings.identificationFields.cpf?.enabled && { key: 'cpf', label: 'CPF', icon: 'mdi:card-account-details-outline', placeholder: '000.000.000-00', type: 'text', required: settings.identificationFields.cpf?.required },
        ].filter(Boolean)
        : [];
    const hasIdentificationStep = enabledIdentificationFields.length > 0;
    const totalPreviewSteps = questions.length + (hasIdentificationStep ? 1 : 0);
    const isOnIdentificationStep = hasIdentificationStep && previewStep === 0;
    const currentQuestionIndex = hasIdentificationStep ? previewStep - 1 : previewStep;

    // Preview theme — Google Forms style, all colors derived from settings.backgroundColor
    const _hexRgb = (hex) => { const h = (hex || '#7C3AED').replace('#', ''); return [parseInt(h.substr(0, 2), 16) || 0, parseInt(h.substr(2, 2), 16) || 0, parseInt(h.substr(4, 2), 16) || 0]; };
    const _mixW = (hex, r) => { const [R, G, B] = _hexRgb(hex); return `rgb(${Math.round(R + (255 - R) * r)}, ${Math.round(G + (255 - G) * r)}, ${Math.round(B + (255 - B) * r)})`; };
    const _darken = (hex, f) => { const [R, G, B] = _hexRgb(hex); return `rgb(${Math.round(R * f)}, ${Math.round(G * f)}, ${Math.round(B * f)})`; };
    const pvAccent = settings.backgroundColor || '#7C3AED';
    const pvIsHex = pvAccent.startsWith('#');
    const pvPageBg = pvIsHex ? _mixW(pvAccent, 0.92) : '#f0ebf8';
    const pvAccentHover = pvIsHex ? _darken(pvAccent, 0.82) : pvAccent;
    const pvAccentSuperLight = pvIsHex ? _mixW(pvAccent, 0.95) : '#f7f3fc';
    const isLightBg = true; // always light background in Google Forms style

    // Dynamic CSS for preview (Google Forms style)
    const previewCSS = `
        .gfp-input{border:0;border-bottom:1px solid #dadce0;padding:8px 0;font-size:14px;outline:none;background:transparent;width:100%;color:#202124;transition:border-color .2s}
        .gfp-input:focus{border-bottom:2px solid ${pvAccent};margin-bottom:-1px}
        .gfp-input::placeholder{color:#80868b}
        .gfp-textarea{border:0;border-bottom:1px solid #dadce0;padding:8px 0;font-size:14px;outline:none;background:transparent;width:100%;color:#202124;min-height:80px;resize:vertical;transition:border-color .2s}
        .gfp-textarea:focus{border-bottom:2px solid ${pvAccent};margin-bottom:-1px}
        .gfp-textarea::placeholder{color:#80868b}
        .gfp-radio,.gfp-checkbox{accent-color:${pvAccent};cursor:pointer}
        .gfp-opt{display:flex;align-items:center;gap:12px;padding:6px 0;cursor:pointer;border-radius:4px}
        .gfp-opt:hover{background:${pvAccentSuperLight}}
        .gfp-card{background:#fff;border-radius:8px;border:1px solid #dadce0;padding:24px;margin-bottom:12px;transition:box-shadow .2s,border-color .2s}
        .gfp-card:hover{box-shadow:0 1px 3px rgba(0,0,0,.08)}
        .gfp-card:focus-within{border-color:${pvAccent};box-shadow:0 2px 6px rgba(0,0,0,.05)}
        .gfp-header{background:#fff;border-radius:8px;border:1px solid #dadce0;overflow:hidden;margin-bottom:12px}
        .gfp-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 24px;border-radius:4px;font-size:14px;font-weight:500;color:#fff;border:none;cursor:pointer;transition:background .2s,box-shadow .2s;background:${pvAccent}}
        .gfp-btn:hover{background:${pvAccentHover};box-shadow:0 1px 3px rgba(0,0,0,.15)}
        .gfp-btn-sec{display:inline-flex;align-items:center;gap:8px;padding:10px 24px;border-radius:4px;font-size:14px;font-weight:500;color:${pvAccent};border:none;cursor:pointer;background:transparent;transition:background .2s}
        .gfp-btn-sec:hover{background:${pvAccentSuperLight}}
        .gfp-link{color:${pvAccent};font-size:13px;font-weight:500;cursor:pointer;background:none;border:none}
        .gfp-link:hover{text-decoration:underline}
        .gfp-block{background:#f8f9fa;border:1px solid #dadce0;border-radius:8px;padding:16px}
        .gfp-block-input{width:100%;border:1px solid #dadce0;border-radius:4px;padding:8px 10px;font-size:13px;outline:none;color:#202124;background:#fff;transition:border-color .2s}
        .gfp-block-input:focus{border-color:${pvAccent}}
        .gfp-block-input::placeholder{color:#80868b}
        .gfp-block-ta{width:100%;border:1px solid #dadce0;border-radius:4px;padding:8px 10px;font-size:13px;outline:none;color:#202124;background:#fff;min-height:60px;resize:none;transition:border-color .2s}
        .gfp-block-ta:focus{border-color:${pvAccent}}
        .gfp-block-ta::placeholder{color:#80868b}
        .gfp-upload{border:2px dashed #dadce0;border-radius:8px;padding:24px;text-align:center;cursor:pointer;transition:border-color .2s,background .2s}
        .gfp-upload:hover{border-color:${pvAccent};background:${pvAccentSuperLight}}
    `;

    // Unified preview field renderer (Google Forms style)
    const renderPreviewField = (q) => {
        switch (q.type) {
            case 'short_text': {
                const stCfg = SHORT_TEXT_INPUT_TYPES.find(t => t.value === q.inputType) || SHORT_TEXT_INPUT_TYPES[0];
                return <input type={stCfg.htmlType} placeholder={stCfg.placeholder} className="gfp-input" />;
            }
            case 'paragraph':
                return <textarea placeholder="Sua resposta" className="gfp-textarea" />;
            case 'multiple_choice':
                return (
                    <div className="space-y-1">
                        {q.options?.map((opt, i) => (
                            <label key={i} className="gfp-opt">
                                <input type="radio" name={`pv-q-${q.id}`} className="gfp-radio w-[18px] h-[18px]" />
                                <span className="text-sm text-[#202124]">{opt}</span>
                            </label>
                        ))}
                    </div>
                );
            case 'checkbox':
                return (
                    <div className="space-y-1">
                        {q.options?.map((opt, i) => (
                            <label key={i} className="gfp-opt">
                                <input type="checkbox" className="gfp-checkbox w-[18px] h-[18px] rounded" />
                                <span className="text-sm text-[#202124]">{opt}</span>
                            </label>
                        ))}
                    </div>
                );
            case 'dropdown':
                return (
                    <Select>
                        <SelectTrigger className="w-full md:w-[300px] border-0 border-b border-[#dadce0] rounded-none px-0 py-2 text-sm text-[#202124] shadow-none focus:ring-0 hover:border-[#80868b] transition-colors h-auto">
                            <SelectValue placeholder="Escolha" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-[#dadce0] rounded shadow-lg z-[9999]">
                            {q.options?.map((opt, i) => (
                                <SelectItem key={i} value={opt} className="py-2 px-3 text-sm text-[#202124] cursor-pointer hover:bg-gray-50">{opt}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );
            case 'info_block': {
                const bCount = getPreviewBlockCount(q.id);
                const bMax = q.blockRepeat || 3;
                return (
                    <div className="space-y-3">
                        {Array.from({ length: bCount }).map((_, ri) => (
                            <div key={ri} className="gfp-block">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-medium text-[#5f6368] uppercase tracking-wider">{q.title || 'Bloco'} #{ri + 1}</p>
                                    {bCount > 1 && (
                                        <button type="button" onClick={() => handlePreviewRemoveBlock(q.id)} className="p-1 rounded text-[#5f6368] hover:text-red-600 hover:bg-red-50 transition-colors">
                                            <Iconify icon="mdi:trash-can-outline" className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {(q.blockFields || []).map((field) => (
                                        <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                                            <label className="block text-xs font-medium text-[#5f6368] mb-1">{field.label}</label>
                                            {field.type === 'textarea' ? (
                                                <textarea placeholder={`${field.label}...`} className="gfp-block-ta" />
                                            ) : field.type === 'date' ? (
                                                <input type="date" className="gfp-block-input" />
                                            ) : (
                                                <input type="text" placeholder={`${field.label}...`} className="gfp-block-input" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {bCount < bMax && (
                            <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePreviewAddBlock(q.id, bMax); }}
                                className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-[#dadce0] rounded-lg text-sm font-medium transition-colors"
                                style={{ color: pvAccent }}
                            >
                                <Iconify icon="mdi:plus-circle-outline" className="w-4 h-4" />
                                Adicionar {q.title || 'bloco'} ({bCount}/{bMax})
                            </button>
                        )}
                    </div>
                );
            }
            case 'file_upload':
                return (
                    <div className="gfp-upload">
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: pvAccentSuperLight }}>
                                <Iconify icon="mdi:cloud-upload-outline" className="w-5 h-5" style={{ color: pvAccent }} />
                            </div>
                            <p className="text-sm font-medium text-[#5f6368]">Clique para enviar arquivo</p>
                            <p className="text-xs text-[#80868b]">Máx. {q.fileUploadConfig?.maxFileSize || 10}MB — {q.fileUploadConfig?.maxFiles || 1} arquivo(s)</p>
                        </div>
                    </div>
                );
            default: return null;
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="flex flex-col h-full items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Iconify icon="mdi:loading" className="w-12 h-12 text-[#7C3AED] animate-spin" />
                    <span className="text-gray-500 dark:text-[#ABABAD]">Carregando formulário...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Top Navigation Bar */}
            <header className="bg-white dark:bg-[#222529] border-b border-gray-200 dark:border-[#383838] shrink-0">
                {/* Linha principal */}
                <div className="h-14 px-4 flex items-center gap-3">
                    {/* Esquerda: Voltar + Nome */}
                    <button
                        onClick={() => navigate('/dashboard/formularios')}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-[#1A1D21] transition-colors shrink-0"
                    >
                        <Iconify icon="eva:arrow-ios-back-fill" width={18} className="text-gray-500 dark:text-[#ABABAD]" />
                    </button>

                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
                            <Iconify icon="mdi:form-select" width={15} className="text-[#7C3AED]" />
                        </div>
                        <input
                            value={title}
                            onChange={(e) => { setTitle(e.target.value); setHasChanges(true); }}
                            className="font-semibold text-gray-800 dark:text-[#D1D2D3] bg-transparent border-none focus:ring-0 focus:outline-none text-sm min-w-0 w-full max-w-[220px] truncate placeholder:text-gray-300 dark:placeholder:text-[#555]"
                            placeholder="Nome do formulário..."
                        />
                        {formStatus === 'draft' && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-md border border-amber-200 dark:border-amber-500/20 shrink-0">
                                Rascunho
                            </span>
                        )}
                        {isPublished && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-md border border-emerald-200 dark:border-emerald-500/20 shrink-0">
                                Publicado
                            </span>
                        )}
                    </div>

                    {/* Separador */}
                    <div className="h-5 w-px bg-gray-200 dark:bg-[#383838] mx-1 shrink-0 hidden md:block" />

                    {/* Centro: Tabs */}
                    <nav className="hidden md:flex items-center gap-0.5 bg-gray-100/80 dark:bg-[#1A1D21] rounded-lg p-0.5 shrink-0">
                        {[
                            { key: 'editor', label: 'Editor', icon: 'mdi:pencil-ruler' },
                            { key: 'configurações', label: 'Configurações', icon: 'mdi:cog-outline' },
                            { key: 'respostas', label: 'Respostas', icon: 'mdi:message-reply-text-outline' },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${activeTab === tab.key
                                    ? 'bg-white dark:bg-[#2A2D31] text-[#7C3AED] shadow-sm'
                                    : 'text-gray-500 dark:text-[#ABABAD] hover:text-gray-700 dark:hover:text-[#D1D2D3]'
                                }`}
                            >
                                <Iconify icon={tab.icon} width={14} />
                                {tab.label}
                            </button>
                        ))}
                    </nav>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Direita: Ações */}
                    <div className="flex items-center gap-1.5">
                        {activeTab === 'editor' && (
                            <button
                                className="hidden md:flex items-center gap-1.5 px-3 h-8 text-xs font-medium text-gray-600 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#1A1D21] rounded-lg transition-colors"
                                onClick={() => {
                                    setPreviewStep(0);
                                    setPreviewBlockCounts({});
                                    setPreviewOpen(true);
                                }}
                            >
                                <Iconify icon="eva:eye-outline" width={15} />
                                Preview
                            </button>
                        )}

                        {activeTab === 'editor' && (
                            <button
                                className="hidden md:flex items-center gap-1.5 px-3 h-8 text-xs font-medium text-gray-600 dark:text-[#ABABAD] hover:bg-gray-100 dark:hover:bg-[#1A1D21] rounded-lg transition-colors relative"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? (
                                    <Iconify icon="mdi:loading" width={15} className="animate-spin" />
                                ) : (
                                    <Iconify icon="mdi:content-save-outline" width={15} />
                                )}
                                {saving ? 'Salvando...' : 'Salvar'}
                                {hasChanges && !saving && (
                                    <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-orange-500 rounded-full" />
                                )}
                            </button>
                        )}

                        {activeTab === 'editor' && (
                            <>
                                <div className="h-5 w-px bg-gray-200 dark:bg-[#383838] mx-0.5 shrink-0 hidden md:block" />
                                <button
                                    className={`hidden md:flex items-center gap-1.5 px-3.5 h-8 text-xs font-semibold rounded-lg transition-all ${isPublished
                                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                        : 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-sm shadow-[#7C3AED]/25'
                                    }`}
                                    onClick={handlePublish}
                                    disabled={saving}
                                >
                                    <Iconify icon={isPublished ? 'mdi:check-circle-outline' : 'mdi:rocket-launch-outline'} width={14} />
                                    {isPublished ? 'Publicado' : 'Publicar'}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Barra de link público */}
                {publicId && activeTab === 'editor' && (
                    <div className="h-9 px-4 flex items-center gap-2 bg-gray-50/80 dark:bg-[#1A1D21]/60 border-t border-gray-100 dark:border-[#2A2D31]">
                        <div className="flex items-center gap-1.5 text-gray-400 dark:text-[#777]">
                            <Iconify icon="mdi:link-variant" width={14} />
                            <span className="text-[11px] font-medium uppercase tracking-wider">Link público</span>
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            <code className="text-[12px] text-gray-600 dark:text-[#ABABAD] font-mono truncate">
                                {`https://link.${verificarUrlProvider()}/forms/${publicId}`}
                            </code>
                        </div>
                        <button
                            className="flex items-center gap-1.5 px-2.5 h-6 text-[11px] font-medium text-[#7C3AED] hover:bg-[#7C3AED]/10 rounded-md transition-colors shrink-0"
                            onClick={() => {
                                navigator.clipboard.writeText(`https://link.${verificarUrlProvider()}/forms/${publicId}`);
                                toast({ title: "Link copiado!" });
                            }}
                        >
                            <Iconify icon="mdi:content-copy" width={12} />
                            Copiar
                        </button>
                        <button
                            className="flex items-center justify-center w-6 h-6 text-gray-400 dark:text-[#777] hover:bg-gray-200/60 dark:hover:bg-[#2A2D31] rounded-md transition-colors shrink-0"
                            onClick={() => {
                                window.open(`https://link.${verificarUrlProvider()}/forms/${publicId}`, '_blank');
                            }}
                            title="Abrir em nova aba"
                        >
                            <Iconify icon="mdi:open-in-new" width={12} />
                        </button>
                    </div>
                )}

                {/* Tabs mobile */}
                <div className="flex md:hidden border-t border-gray-100 dark:border-[#2A2D31]">
                    {[
                        { key: 'editor', label: 'Editor', icon: 'mdi:pencil-ruler' },
                        { key: 'configurações', label: 'Config.', icon: 'mdi:cog-outline' },
                        { key: 'respostas', label: 'Respostas', icon: 'mdi:message-reply-text-outline' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${activeTab === tab.key
                                ? 'text-[#7C3AED] border-b-2 border-[#7C3AED]'
                                : 'text-gray-400 dark:text-[#777]'
                            }`}
                        >
                            <Iconify icon={tab.icon} width={14} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </header>

            {activeTab === 'editor' && (
            <div className="flex-1 overflow-y-auto">
                <div className="w-full max-w-3xl mx-auto py-6 px-4 space-y-3">

                    {/* Card do Título e Descrição */}
                    <Card className={`shadow-sm border bg-white dark:bg-[#222529] overflow-hidden transition-all ${activeQuestion === '_header' ? 'border-l-4 border-l-[#7C3AED] border-t-gray-200 dark:border-t-[#383838] border-r-gray-200 dark:border-r-[#383838] border-b-gray-200 dark:border-b-[#383838]' : 'border-gray-200 dark:border-[#383838]'}`}
                        onClick={() => setActiveQuestion('_header')}
                    >
                        <div className="h-2.5 bg-[#7C3AED] rounded-t-none" />
                        <CardContent className="p-6">
                            <div className="space-y-3">
                                <Input
                                    value={title}
                                    onChange={(e) => { setTitle(e.target.value); setHasChanges(true); }}
                                    className="text-2xl font-medium border-transparent hover:border-gray-200 dark:hover:border-[#383838] focus:border-[#7C3AED] px-0 h-auto py-2 rounded-none transition-all placeholder:text-gray-300 dark:placeholder:text-[#ABABAD] dark:text-[#D1D2D3]"
                                    placeholder="Título do formulário"
                                />
                                <Input
                                    value={description}
                                    onChange={(e) => { setDescription(e.target.value); markAsChanged(); }}
                                    className="text-sm text-gray-500 dark:text-[#ABABAD] border-transparent hover:border-gray-200 dark:hover:border-[#383838] focus:border-[#7C3AED] px-0 h-auto py-1 rounded-none transition-all placeholder:text-gray-400 dark:placeholder:text-[#ABABAD]"
                                    placeholder="Descrição do formulário (opcional)"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Lista de Perguntas - Drag & Drop */}
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="questions">
                            {(provided) => (
                                <div
                                    className="space-y-3"
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {questions.map((question, index) => (
                                        <Draggable key={question.id} draggableId={question.id} index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    onClick={() => setActiveQuestion(question.id)}
                                                >
                                                    <Card className={`shadow-sm bg-white dark:bg-[#222529] overflow-hidden transition-all ${
                                                        activeQuestion === question.id
                                                            ? 'border-l-4 border-l-[#7C3AED] border-t-gray-200 dark:border-t-[#383838] border-r-gray-200 dark:border-r-[#383838] border-b-gray-200 dark:border-b-[#383838]'
                                                            : 'border border-gray-200 dark:border-[#383838] hover:border-gray-300 dark:hover:border-[#565856]'
                                                        } ${snapshot.isDragging ? 'shadow-lg ring-2 ring-[#7C3AED]/20' : ''}`}
                                                    >
                                                        <CardContent className="p-6">
                                                            {/* Modo ativo - editável */}
                                                            {activeQuestion === question.id ? (
                                                                <div className="space-y-4">
                                                                    {/* Linha: Input título + Select tipo */}
                                                                    <div className="flex items-start gap-4">
                                                                        <div className="flex-1 space-y-1">
                                                                            <Input
                                                                                value={question.title}
                                                                                onChange={(e) => handleUpdateQuestion(question.id, 'title', e.target.value)}
                                                                                className="text-base font-medium border-transparent bg-gray-50 dark:bg-[#1A1D21] hover:border-gray-200 dark:hover:border-[#383838] focus:border-[#7C3AED] focus:bg-white dark:focus:bg-[#222529] px-3 h-12 rounded-lg transition-all placeholder:text-gray-400 dark:placeholder:text-[#ABABAD] dark:text-[#D1D2D3]"
                                                                                placeholder="Pergunta"
                                                                            />
                                                                            <Input
                                                                                value={question.description || ''}
                                                                                onChange={(e) => handleUpdateQuestion(question.id, 'description', e.target.value)}
                                                                                className="text-sm border-transparent hover:border-gray-200 dark:hover:border-[#383838] focus:border-[#7C3AED] px-3 h-9 rounded-lg transition-all placeholder:text-gray-400 dark:placeholder:text-[#ABABAD] text-gray-500 dark:text-[#ABABAD]"
                                                                                placeholder="Descrição (opcional)"
                                                                            />
                                                                        </div>
                                                                        <div className="w-[200px]">
                                                                            <Select
                                                                                value={question.type}
                                                                                onValueChange={(value) => handleUpdateQuestion(question.id, 'type', value)}
                                                                            >
                                                                                <SelectTrigger className="h-12 bg-gray-50 dark:bg-[#1A1D21] border-transparent text-gray-600 dark:text-[#ABABAD] hover:border-gray-200 dark:hover:border-[#383838]">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <Iconify icon={QUESTION_TYPES.find(t => t.value === question.type)?.icon} />
                                                                                        <SelectValue />
                                                                                    </div>
                                                                                </SelectTrigger>
                                                                                <SelectContent>
                                                                                    {QUESTION_TYPES.map(type => (
                                                                                        <SelectItem key={type.value} value={type.value}>
                                                                                            <div className="flex items-center gap-2">
                                                                                                <Iconify icon={type.icon} className="text-slate-400" />
                                                                                                {type.label}
                                                                                            </div>
                                                                                        </SelectItem>
                                                                                    ))}
                                                                                </SelectContent>
                                                                            </Select>
                                                                        </div>
                                                                    </div>

                                                                    {/* Seletor de tipo de campo para Resposta Curta */}
                                                                    {question.type === 'short_text' && (
                                                                        <div className="space-y-2">
                                                                            <p className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider">Tipo do campo</p>
                                                                            <div className="grid grid-cols-4 gap-2">
                                                                                {SHORT_TEXT_INPUT_TYPES.map(inputType => {
                                                                                    const isSelected = (question.inputType || 'text') === inputType.value;
                                                                                    return (
                                                                                        <button
                                                                                            key={inputType.value}
                                                                                            type="button"
                                                                                            onClick={(e) => { e.stopPropagation(); handleUpdateQuestion(question.id, 'inputType', inputType.value); }}
                                                                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                                                                                                isSelected
                                                                                                    ? 'border-[#7C3AED] bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]'
                                                                                                    : 'border-gray-200 dark:border-[#383838] bg-white dark:bg-[#222529] text-gray-600 dark:text-[#ABABAD] hover:border-gray-300 dark:hover:border-[#565856] hover:bg-gray-50 dark:hover:bg-[#383838]'
                                                                                            }`}
                                                                                        >
                                                                                            <Iconify icon={inputType.icon} width={16} className={isSelected ? 'text-[#7C3AED]' : 'text-gray-400'} />
                                                                                            {inputType.label}
                                                                                        </button>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Inputs das opções / Editor de campos */}
                                                                    {question.type === 'info_block' ? (
                                                                        <div className="space-y-4">
                                                                            {/* Configuração de repetições */}
                                                                            <div className="flex items-center gap-3 bg-violet-50 dark:bg-violet-500/10 rounded-lg p-3">
                                                                                <Iconify icon="mdi:repeat" width={18} className="text-violet-500" />
                                                                                <span className="text-xs font-medium text-gray-600 dark:text-[#ABABAD]">Repetições:</span>
                                                                                <Select
                                                                                    value={String(question.blockRepeat || 3)}
                                                                                    onValueChange={(val) => handleUpdateQuestion(question.id, 'blockRepeat', parseInt(val))}
                                                                                >
                                                                                    <SelectTrigger className="h-8 w-20 text-xs bg-white dark:bg-[#222529] border-gray-200 dark:border-[#383838]">
                                                                                        <SelectValue />
                                                                                    </SelectTrigger>
                                                                                    <SelectContent>
                                                                                        {[1, 2, 3, 4, 5].map(n => (
                                                                                            <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                                                                                        ))}
                                                                                    </SelectContent>
                                                                                </Select>
                                                                                <span className="text-[10px] text-gray-400 dark:text-[#ABABAD]">O respondente poderá preencher o bloco este número de vezes</span>
                                                                            </div>

                                                                            {/* Lista de campos do bloco */}
                                                                            <div className="space-y-2">
                                                                                <p className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider">Campos do bloco</p>
                                                                                {(question.blockFields || []).map((field, fIndex) => (
                                                                                    <div key={field.id} className="flex items-center gap-2 group bg-gray-50 dark:bg-[#1A1D21] rounded-lg p-2">
                                                                                        <span className="text-xs font-semibold text-gray-400 dark:text-[#ABABAD] w-5 text-center">{fIndex + 1}</span>
                                                                                        <Input
                                                                                            value={field.label}
                                                                                            onChange={(e) => handleUpdateBlockField(question.id, field.id, 'label', e.target.value)}
                                                                                            className="flex-1 h-8 text-sm border-transparent hover:border-gray-200 dark:hover:border-[#383838] focus:border-[#7C3AED] bg-white dark:bg-[#222529] dark:text-[#D1D2D3]"
                                                                                            placeholder="Nome do campo"
                                                                                        />
                                                                                        <Select
                                                                                            value={field.type}
                                                                                            onValueChange={(val) => handleUpdateBlockField(question.id, field.id, 'type', val)}
                                                                                        >
                                                                                            <SelectTrigger className="h-8 w-[130px] text-xs bg-white dark:bg-[#222529] border-transparent hover:border-gray-200 dark:hover:border-[#383838] dark:text-[#D1D2D3]">
                                                                                                <SelectValue />
                                                                                            </SelectTrigger>
                                                                                            <SelectContent>
                                                                                                {BLOCK_FIELD_TYPES.map(t => (
                                                                                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                                                                                ))}
                                                                                            </SelectContent>
                                                                                        </Select>
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="icon"
                                                                                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
                                                                                            onClick={(e) => { e.stopPropagation(); handleRemoveBlockField(question.id, field.id); }}
                                                                                        >
                                                                                            <Iconify icon="mdi:close" width={16} />
                                                                                        </Button>
                                                                                    </div>
                                                                                ))}
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-500/10 h-8 text-xs"
                                                                                    onClick={(e) => { e.stopPropagation(); handleAddBlockField(question.id); }}
                                                                                >
                                                                                    <Iconify icon="mdi:plus" className="mr-1" width={16} />
                                                                                    Adicionar campo
                                                                                </Button>
                                                                            </div>

                                                                            {/* Preview do bloco */}
                                                                            {(question.blockFields || []).length > 0 && (
                                                                                <div className="border border-dashed border-gray-200 dark:border-[#383838] rounded-lg p-4 bg-white dark:bg-[#222529]">
                                                                                    <p className="text-[10px] font-semibold text-gray-400 dark:text-[#ABABAD] uppercase tracking-wider mb-3">Preview - {question.title || 'Bloco'} #1</p>
                                                                                    <div className="space-y-2">
                                                                                        {(question.blockFields || []).map((field) => (
                                                                                            <div key={field.id} className="space-y-1">
                                                                                                <label className="text-xs text-gray-500 dark:text-[#ABABAD] font-medium">{field.label}</label>
                                                                                                {field.type === 'textarea' ? (
                                                                                                    <div className="w-full h-12 bg-gray-50 dark:bg-[#1A1D21] border border-gray-200 dark:border-[#383838] rounded-md" />
                                                                                                ) : field.type === 'date' ? (
                                                                                                    <div className="w-48 h-8 bg-gray-50 dark:bg-[#1A1D21] border border-gray-200 dark:border-[#383838] rounded-md px-3 flex items-center text-xs text-gray-300 dark:text-[#ABABAD] gap-2">
                                                                                                        <Iconify icon="mdi:calendar" width={14} /> dd/mm/aaaa
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <div className="w-full h-8 bg-gray-50 dark:bg-[#1A1D21] border border-gray-200 dark:border-[#383838] rounded-md" />
                                                                                                )}
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : question.type === 'file_upload' ? (
                                                                        <div className="space-y-4">
                                                                            {/* Config de upload */}
                                                                            <div className="space-y-3">
                                                                                <div className="flex items-center gap-3 bg-violet-50 dark:bg-violet-500/10 rounded-lg p-3">
                                                                                    <Iconify icon="mdi:file-multiple-outline" width={18} className="text-violet-500" />
                                                                                    <span className="text-xs font-medium text-gray-600 dark:text-[#ABABAD]">Máx. arquivos:</span>
                                                                                    <Select
                                                                                        value={String(question.fileUploadConfig?.maxFiles || 1)}
                                                                                        onValueChange={(val) => handleUpdateQuestion(question.id, 'fileUploadConfig', { ...question.fileUploadConfig, maxFiles: parseInt(val) })}
                                                                                    >
                                                                                        <SelectTrigger className="h-8 w-20 text-xs bg-white dark:bg-[#222529] border-gray-200 dark:border-[#383838]">
                                                                                            <SelectValue />
                                                                                        </SelectTrigger>
                                                                                        <SelectContent>
                                                                                            {[1, 2, 3, 4, 5, 10].map(n => (
                                                                                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                                                                            ))}
                                                                                        </SelectContent>
                                                                                    </Select>
                                                                                </div>
                                                                                <div className="flex items-center gap-3 bg-violet-50 dark:bg-violet-500/10 rounded-lg p-3">
                                                                                    <Iconify icon="mdi:weight" width={18} className="text-violet-500" />
                                                                                    <span className="text-xs font-medium text-gray-600 dark:text-[#ABABAD]">Tamanho máx.:</span>
                                                                                    <Select
                                                                                        value={String(question.fileUploadConfig?.maxFileSize || 10)}
                                                                                        onValueChange={(val) => handleUpdateQuestion(question.id, 'fileUploadConfig', { ...question.fileUploadConfig, maxFileSize: parseInt(val) })}
                                                                                    >
                                                                                        <SelectTrigger className="h-8 w-24 text-xs bg-white dark:bg-[#222529] border-gray-200 dark:border-[#383838]">
                                                                                            <SelectValue />
                                                                                        </SelectTrigger>
                                                                                        <SelectContent>
                                                                                            {[5, 10, 25].map(n => (
                                                                                                <SelectItem key={n} value={String(n)}>{n} MB</SelectItem>
                                                                                            ))}
                                                                                        </SelectContent>
                                                                                    </Select>
                                                                                </div>
                                                                            </div>

                                                                            {/* Tipos aceitos */}
                                                                            <div className="space-y-2">
                                                                                <p className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider">Tipos aceitos</p>
                                                                                <div className="grid grid-cols-2 gap-2">
                                                                                    {[
                                                                                        { label: 'Imagens', value: 'image/*', icon: 'mdi:image-outline' },
                                                                                        { label: 'PDF', value: 'application/pdf', icon: 'mdi:file-pdf-box' },
                                                                                        { label: 'Word', value: 'application/msword', icon: 'mdi:file-word-outline' },
                                                                                        { label: 'Planilhas', value: 'application/vnd.ms-excel', icon: 'mdi:file-excel-outline' },
                                                                                    ].map(opt => {
                                                                                        const types = question.fileUploadConfig?.acceptedTypes || [];
                                                                                        const isSelected = types.includes(opt.value);
                                                                                        return (
                                                                                            <button
                                                                                                key={opt.value}
                                                                                                type="button"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    const current = question.fileUploadConfig?.acceptedTypes || [];
                                                                                                    const updated = isSelected
                                                                                                        ? current.filter(t => t !== opt.value)
                                                                                                        : [...current, opt.value];
                                                                                                    handleUpdateQuestion(question.id, 'fileUploadConfig', { ...question.fileUploadConfig, acceptedTypes: updated });
                                                                                                }}
                                                                                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                                                                                                    isSelected
                                                                                                        ? 'border-[#7C3AED] bg-violet-50 text-[#7C3AED]'
                                                                                                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                                                                                }`}
                                                                                            >
                                                                                                <Iconify icon={opt.icon} width={16} className={isSelected ? 'text-[#7C3AED]' : 'text-gray-400'} />
                                                                                                {opt.label}
                                                                                            </button>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                                <p className="text-[10px] text-gray-400 dark:text-[#ABABAD]">Deixe vazio para aceitar todos os tipos permitidos</p>
                                                                            </div>

                                                                            {/* Preview do dropzone */}
                                                                            {renderQuestionInput(question)}
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            {renderQuestionInput(question)}
                                                                        </div>
                                                                    )}

                                                                    {/* Barra de ações inferior */}
                                                                    <Separator />
                                                                    <div className="flex items-center justify-between pt-1">
                                                                        <div
                                                                            {...provided.dragHandleProps}
                                                                            className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 dark:hover:bg-[#383838] transition-colors"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <Iconify icon="mdi:drag" width={20} className="text-gray-400" />
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600" onClick={(e) => { e.stopPropagation(); handleDuplicateQuestion(question); }} title="Duplicar">
                                                                                <Iconify icon="mdi:content-copy" width={18} />
                                                                            </Button>
                                                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(question.id); }} title="Excluir">
                                                                                <Iconify icon="mdi:trash-can-outline" width={18} />
                                                                            </Button>
                                                                            <div className="w-px h-5 bg-gray-200 dark:bg-[#383838] mx-1" />
                                                                            <div className="flex items-center gap-2">
                                                                                <Label htmlFor={`req-${question.id}`} className="text-xs text-gray-500 dark:text-[#ABABAD]">Obrigatória</Label>
                                                                                <Switch
                                                                                    id={`req-${question.id}`}
                                                                                    checked={question.required}
                                                                                    onCheckedChange={(checked) => handleUpdateQuestion(question.id, 'required', checked)}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                /* Modo inativo - visualização compacta */
                                                                <div className="space-y-3">
                                                                    <div>
                                                                        <p className="text-sm text-gray-800 dark:text-[#D1D2D3]">
                                                                            {question.title || 'Pergunta sem título'}
                                                                            {question.required && <span className="text-red-500 ml-1">*</span>}
                                                                        </p>
                                                                        {question.description && (
                                                                            <p className="text-xs text-gray-400 mt-0.5">{question.description}</p>
                                                                        )}
                                                                    </div>
                                                                    <div className="opacity-60 pointer-events-none">
                                                                        {renderQuestionInput(question)}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>

                    {/* Botão Adicionar Pergunta */}
                    <div className="flex justify-center pt-2 pb-6">
                        <Button
                            variant="outline"
                            className="border-dashed border-gray-300 dark:border-[#383838] text-gray-500 dark:text-[#ABABAD] hover:border-[#7C3AED] hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 px-6"
                            onClick={handleAddQuestion}
                        >
                            <Iconify icon="mdi:plus" className="mr-2" />
                            Adicionar pergunta
                        </Button>
                    </div>

                </div>
            </div>
            )}

            {/* ==================== ABA CONFIGURAÇÕES ==================== */}
            {activeTab === 'configurações' && (
                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50 dark:bg-[#1A1D21]">
                    <div className="max-w-2xl mx-auto space-y-6">

                        {/* Card de Logo */}
                        <Card className="bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838]">
                            <CardContent className="p-6 space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="p-2.5 bg-pink-100 dark:bg-pink-500/10 rounded-lg mt-0.5">
                                        <Iconify icon="mdi:image-outline" className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-[#D1D2D3]">Logo do formulário</h3>
                                        <p className="text-xs text-gray-500 dark:text-[#ABABAD] mt-0.5">
                                            Adicione um logo que será exibido acima do título
                                        </p>
                                    </div>
                                </div>

                                {settings.logoUrl ? (
                                    <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-[#1A1D21] rounded-xl border border-gray-100 dark:border-[#383838]">
                                        <img
                                            src={settings.logoUrl}
                                            alt="Logo"
                                            className="w-16 h-16 object-contain rounded-lg border border-gray-200 dark:border-[#383838] bg-white"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-700 dark:text-[#D1D2D3] truncate">Logo atual</p>
                                            <p className="text-xs text-gray-400 truncate">{settings.logoUrl}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    await formulariosService.removerLogo(formId);
                                                    setSettings(prev => ({ ...prev, logoUrl: null }));
                                                    markAsChanged();
                                                    toast({ title: 'Logo removido' });
                                                } catch (err) {
                                                    toast({ title: 'Erro ao remover logo', variant: 'destructive' });
                                                }
                                            }}
                                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                                            title="Remover logo"
                                        >
                                            <Iconify icon="mdi:trash-can-outline" className="w-5 h-5" />
                                        </button>
                                    </div>
                                ) : (
                                    <label className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-dashed transition-all cursor-pointer ${
                                        uploadingLogo
                                            ? 'border-gray-200 dark:border-[#383838] bg-gray-50 dark:bg-[#1A1D21]'
                                            : 'border-gray-300 dark:border-[#383838] hover:border-[#7C3AED] hover:bg-violet-50/50 dark:hover:bg-violet-500/5'
                                    }`}>
                                        {uploadingLogo ? (
                                            <>
                                                <Iconify icon="mdi:loading" className="w-8 h-8 text-[#7C3AED] animate-spin" />
                                                <p className="text-sm text-gray-500">Enviando...</p>
                                            </>
                                        ) : (
                                            <>
                                                <div className="p-3 bg-violet-100 dark:bg-violet-500/10 rounded-xl">
                                                    <Iconify icon="mdi:cloud-upload-outline" className="w-6 h-6 text-[#7C3AED]" />
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-sm font-medium text-gray-700 dark:text-[#D1D2D3]">Clique para enviar</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">PNG, JPG, GIF ou WebP</p>
                                                </div>
                                            </>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                            className="hidden"
                                            disabled={uploadingLogo}
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                setUploadingLogo(true);
                                                try {
                                                    const res = await formulariosService.uploadLogo(formId, file);
                                                    if (res.data?.url) {
                                                        setSettings(prev => ({ ...prev, logoUrl: res.data.url }));
                                                        markAsChanged();
                                                        toast({ title: 'Logo enviado com sucesso' });
                                                    }
                                                } catch (err) {
                                                    toast({ title: 'Erro ao enviar logo', description: err.message, variant: 'destructive' });
                                                } finally {
                                                    setUploadingLogo(false);
                                                    e.target.value = '';
                                                }
                                            }}
                                        />
                                    </label>
                                )}
                            </CardContent>
                        </Card>

                        {/* Card de Modo de Exibição */}
                        <Card className="bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838]">
                            <CardContent className="p-6 space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="p-2.5 bg-blue-100 dark:bg-blue-500/10 rounded-lg mt-0.5">
                                        <Iconify icon="mdi:view-carousel-outline" className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-[#D1D2D3]">Modo de exibição</h3>
                                        <p className="text-xs text-gray-500 dark:text-[#ABABAD] mt-0.5">
                                            Escolha como as perguntas serão apresentadas ao respondente
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => { setIsStepByStep(false); markAsChanged(); }}
                                        className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                                            !isStepByStep
                                                ? 'border-[#7C3AED] bg-violet-50 dark:bg-violet-500/10'
                                                : 'border-gray-200 dark:border-[#383838] hover:border-gray-300 dark:hover:border-[#565856] bg-white dark:bg-[#222529]'
                                        }`}
                                    >
                                        <div className={`p-3 rounded-lg ${!isStepByStep ? 'bg-[#7C3AED]/10' : 'bg-gray-100 dark:bg-[#383838]'}`}>
                                            <Iconify icon="mdi:format-list-bulleted" className={`w-6 h-6 ${!isStepByStep ? 'text-[#7C3AED]' : 'text-gray-400'}`} />
                                        </div>
                                        <div className="text-center">
                                            <p className={`text-sm font-semibold ${!isStepByStep ? 'text-[#7C3AED]' : 'text-gray-700 dark:text-[#ABABAD]'}`}>Simples</p>
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD] mt-0.5">Todas as perguntas na mesma página</p>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => { setIsStepByStep(true); markAsChanged(); }}
                                        className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                                            isStepByStep
                                                ? 'border-[#7C3AED] bg-violet-50 dark:bg-violet-500/10'
                                                : 'border-gray-200 dark:border-[#383838] hover:border-gray-300 dark:hover:border-[#565856] bg-white dark:bg-[#222529]'
                                        }`}
                                    >
                                        <div className={`p-3 rounded-lg ${isStepByStep ? 'bg-[#7C3AED]/10' : 'bg-gray-100 dark:bg-[#383838]'}`}>
                                            <Iconify icon="mdi:page-next-outline" className={`w-6 h-6 ${isStepByStep ? 'text-[#7C3AED]' : 'text-gray-400'}`} />
                                        </div>
                                        <div className="text-center">
                                            <p className={`text-sm font-semibold ${isStepByStep ? 'text-[#7C3AED]' : 'text-gray-700 dark:text-[#ABABAD]'}`}>Step-by-Step</p>
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD] mt-0.5">Uma pergunta por vez, com transições</p>
                                        </div>
                                    </button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Card de Resposta Única */}
                        <Card className="bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838]">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2.5 bg-amber-100 dark:bg-amber-500/10 rounded-lg mt-0.5">
                                            <Iconify icon="mdi:lock-outline" className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-800 dark:text-[#D1D2D3]">Limitar a uma resposta</h3>
                                            <p className="text-xs text-gray-500 dark:text-[#ABABAD] mt-0.5">
                                                Cada pessoa poderá responder o formulário apenas uma vez. A identificação é feita por e-mail, CPF ou IP.
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={!settings.allowMultipleResponses}
                                        onCheckedChange={(checked) => {
                                            setSettings(prev => ({ ...prev, allowMultipleResponses: !checked }));
                                            markAsChanged();
                                        }}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Card de Identificação do Respondente */}
                        <Card className="bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838]">
                            <CardContent className="p-6 space-y-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2.5 bg-violet-100 dark:bg-violet-500/10 rounded-lg mt-0.5">
                                            <Iconify icon="mdi:account-card-details-outline" className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-800 dark:text-[#D1D2D3]">Identificação do Respondente</h3>
                                            <p className="text-xs text-gray-500 dark:text-[#ABABAD] mt-0.5">
                                                Exigir que o respondente se identifique antes de preencher o formulário
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={settings.requireIdentification}
                                        onCheckedChange={(checked) => {
                                            setSettings(prev => ({ ...prev, requireIdentification: checked }));
                                            markAsChanged();
                                        }}
                                    />
                                </div>

                                {settings.requireIdentification && (
                                    <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-[#383838]">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Campos de identificação
                                        </p>

                                        {[
                                            { key: 'name', label: 'Nome', icon: 'mdi:account-outline', placeholder: 'Nome completo' },
                                            { key: 'email', label: 'E-mail', icon: 'mdi:email-outline', placeholder: 'email@exemplo.com' },
                                            { key: 'phone', label: 'Telefone', icon: 'mdi:phone-outline', placeholder: '(00) 00000-0000' },
                                            { key: 'cpf', label: 'CPF', icon: 'mdi:card-account-details-outline', placeholder: '000.000.000-00' },
                                        ].map(field => {
                                            const fieldSettings = settings.identificationFields?.[field.key] || { enabled: false, required: false };
                                            return (
                                                <div
                                                    key={field.key}
                                                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${fieldSettings.enabled
                                                        ? 'border-violet-200 dark:border-violet-500/30 bg-violet-50/50 dark:bg-violet-500/5'
                                                        : 'border-gray-100 dark:border-[#383838] bg-gray-50/50 dark:bg-[#1A1D21]/50'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`p-1.5 rounded-md ${fieldSettings.enabled ? 'bg-violet-100 dark:bg-violet-500/10' : 'bg-gray-100 dark:bg-[#383838]'}`}>
                                                            <Iconify icon={field.icon} className={`w-4 h-4 ${fieldSettings.enabled ? 'text-violet-600' : 'text-gray-400'}`} />
                                                        </div>
                                                        <div>
                                                            <p className={`text-sm font-medium ${fieldSettings.enabled ? 'text-gray-800 dark:text-[#D1D2D3]' : 'text-gray-500 dark:text-[#ABABAD]'}`}>
                                                                {field.label}
                                                            </p>
                                                            <p className="text-[10px] text-gray-400">{field.placeholder}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        {fieldSettings.enabled && (
                                                            <label className="flex items-center gap-1.5 cursor-pointer">
                                                                <Checkbox
                                                                    checked={fieldSettings.required}
                                                                    onCheckedChange={(checked) => {
                                                                        setSettings(prev => ({
                                                                            ...prev,
                                                                            identificationFields: {
                                                                                ...prev.identificationFields,
                                                                                [field.key]: { ...prev.identificationFields[field.key], required: !!checked }
                                                                            }
                                                                        }));
                                                                        markAsChanged();
                                                                    }}
                                                                    className="w-3.5 h-3.5 border-violet-300 data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600"
                                                                />
                                                                <span className="text-[10px] text-gray-500 font-medium">Obrigatório</span>
                                                            </label>
                                                        )}
                                                        <Switch
                                                            checked={fieldSettings.enabled}
                                                            onCheckedChange={(checked) => {
                                                                setSettings(prev => ({
                                                                    ...prev,
                                                                    identificationFields: {
                                                                        ...prev.identificationFields,
                                                                        [field.key]: {
                                                                            enabled: checked,
                                                                            required: checked ? prev.identificationFields[field.key]?.required : false
                                                                        }
                                                                    }
                                                                }));
                                                                markAsChanged();
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <p className="text-[10px] text-gray-400 pt-1">
                                            Campos habilitados serão exibidos antes do formulário. Campos obrigatórios impedirão o envio se não preenchidos.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Botão Salvar - Configurações */}
                        <div className="flex justify-end">
                            <Button
                                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <Iconify icon="mdi:loading" className="mr-2 animate-spin" />
                                        Salvando...
                                    </>
                                ) : (
                                    <>
                                        <Iconify icon="mdi:content-save" className="mr-2" />
                                        Salvar Configurações
                                        {hasChanges && <span className="ml-2 w-2 h-2 bg-orange-400 rounded-full"></span>}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== ABA RESPOSTAS ==================== */}
            {activeTab === 'respostas' && (
                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50 dark:bg-[#1A1D21]">
                    <div className="mx-auto flex gap-6 h-full">

                        {/* ===== COLUNA ESQUERDA — Resumo & Gráfico ===== */}
                        <div className="w-[280px] flex-shrink-0 space-y-3 sticky top-0 self-start">

                            {/* Stats Cards — 1 por linha */}
                            <Card className="bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838]">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-violet-100 dark:bg-violet-500/10 rounded-lg">
                                            <Iconify icon="mdi:message-reply-text" className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD] font-medium leading-tight">Total de Respostas</p>
                                            <p className="text-xl font-bold text-gray-800 dark:text-[#D1D2D3]">{estatisticas?.totalRespostas ?? 0}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838]">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-green-100 dark:bg-green-500/10 rounded-lg">
                                            <Iconify icon="mdi:check-circle" className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD] font-medium leading-tight">Completas</p>
                                            <p className="text-xl font-bold text-gray-800 dark:text-[#D1D2D3]">{estatisticas?.respostasCompletas ?? 0}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838]">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-amber-100 dark:bg-amber-500/10 rounded-lg">
                                            <Iconify icon="mdi:percent" className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD] font-medium leading-tight">Taxa de Completude</p>
                                            <p className="text-xl font-bold text-gray-800 dark:text-[#D1D2D3]">{estatisticas?.taxaCompletude ?? 0}%</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838]">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-100 dark:bg-blue-500/10 rounded-lg">
                                            <Iconify icon="mdi:clock-outline" className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD] font-medium leading-tight">Duração Média</p>
                                            <p className="text-xl font-bold text-gray-800 dark:text-[#D1D2D3]">{formatDuration(estatisticas?.duracaoMedia)}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Gráfico de Respostas por Dia */}
                            {estatisticas?.respostasPorDia?.length > 0 && (
                                <Card className="bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838]">
                                    <CardContent className="p-4">
                                        <h3 className="text-xs font-semibold text-gray-700 dark:text-[#D1D2D3] mb-3">Últimos 30 dias</h3>
                                        <div className="flex items-end gap-[2px] h-24">
                                            {estatisticas.respostasPorDia.map((day, i) => {
                                                const maxCount = Math.max(...estatisticas.respostasPorDia.map(d => d.count), 1);
                                                const heightPct = (day.count / maxCount) * 100;
                                                return (
                                                    <div key={i} className="flex-1 flex flex-col items-center group relative">
                                                        <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                                            {day._id}: {day.count}
                                                        </div>
                                                        <div
                                                            className="w-full bg-violet-400 hover:bg-violet-500 rounded-t transition-all min-h-[2px]"
                                                            style={{ height: `${Math.max(heightPct, 2)}%` }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {/* ===== COLUNA DIREITA — Tabela de Respostas ===== */}
                        <Card className="flex-1 min-w-0 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] flex flex-col max-h-[calc(100vh-200px)]">
                            <CardContent className="p-0 flex flex-col min-h-0 flex-1">
                                {/* Barra de Filtros */}
                                <div className="px-5 py-4 border-b border-gray-100 dark:border-[#383838] flex items-center justify-between shrink-0">
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-[#D1D2D3]">Respostas</h3>
                                        {/* Toggle View Mode */}
                                        <div className="flex items-center bg-gray-100 dark:bg-[#1A1D21] rounded-lg p-0.5">
                                            <button
                                                onClick={() => setRespostasViewMode('resumo')}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${respostasViewMode === 'resumo' ? 'bg-white dark:bg-[#222529] text-gray-800 dark:text-[#D1D2D3] shadow-sm' : 'text-gray-500 dark:text-[#ABABAD] hover:text-gray-700 dark:hover:text-[#D1D2D3]'}`}
                                            >
                                                <Iconify icon="mdi:view-list" className="w-3.5 h-3.5" />
                                                Resumo
                                            </button>
                                            <button
                                                onClick={() => setRespostasViewMode('planilha')}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${respostasViewMode === 'planilha' ? 'bg-white dark:bg-[#222529] text-gray-800 dark:text-[#D1D2D3] shadow-sm' : 'text-gray-500 dark:text-[#ABABAD] hover:text-gray-700 dark:hover:text-[#D1D2D3]'}`}
                                            >
                                                <Iconify icon="mdi:table" className="w-3.5 h-3.5" />
                                                Planilha
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Select value={filtroStatus} onValueChange={(val) => { setFiltroStatus(val); }}>
                                            <SelectTrigger className="h-8 w-[160px] text-xs">
                                                <SelectValue placeholder="Filtrar status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">Todos os status</SelectItem>
                                                <SelectItem value="completed">Completas</SelectItem>
                                                <SelectItem value="started">Iniciadas</SelectItem>
                                                <SelectItem value="abandoned">Abandonadas</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" size="sm" className={`h-8 text-xs gap-1.5 ${(filtroDataInicio || filtroDataFim) ? 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-400' : ''}`}>
                                                    <Iconify icon="mdi:calendar-range" className="w-3.5 h-3.5" />
                                                    {filtroDataInicio || filtroDataFim ? (
                                                        <>
                                                            {filtroDataInicio ? new Date(filtroDataInicio + 'T00:00:00').toLocaleDateString('pt-BR') : '...'}
                                                            {' — '}
                                                            {filtroDataFim ? new Date(filtroDataFim + 'T00:00:00').toLocaleDateString('pt-BR') : '...'}
                                                        </>
                                                    ) : 'Período'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-3" align="end">
                                                <div className="space-y-3">
                                                    <p className="text-xs font-semibold text-gray-600 dark:text-[#D1D2D3]">Filtrar por período</p>
                                                    <div className="flex items-center gap-2">
                                                        <div className="space-y-1">
                                                            <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">De</label>
                                                            <Input
                                                                type="date"
                                                                value={filtroDataInicio}
                                                                onChange={(e) => setFiltroDataInicio(e.target.value)}
                                                                className="h-8 text-xs w-[140px]"
                                                            />
                                                        </div>
                                                        <span className="text-gray-400 mt-4">—</span>
                                                        <div className="space-y-1">
                                                            <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Até</label>
                                                            <Input
                                                                type="date"
                                                                value={filtroDataFim}
                                                                onChange={(e) => setFiltroDataFim(e.target.value)}
                                                                className="h-8 text-xs w-[140px]"
                                                            />
                                                        </div>
                                                    </div>
                                                    {(filtroDataInicio || filtroDataFim) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => { setFiltroDataInicio(''); setFiltroDataFim(''); }}
                                                            className="text-[11px] text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1 transition-colors"
                                                        >
                                                            <Iconify icon="mdi:close-circle-outline" className="w-3 h-3" />
                                                            Limpar período
                                                        </button>
                                                    )}
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                        {respostas.length > 0 && (
                                            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleExportCSV}>
                                                <Iconify icon="mdi:download" className="mr-1 w-3.5 h-3.5" />
                                                Exportar CSV
                                            </Button>
                                        )}
                                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => loadRespostas(1)}>
                                            <Iconify icon="mdi:refresh" className="mr-1 w-3.5 h-3.5" />
                                            Atualizar
                                        </Button>
                                    </div>
                                </div>

                                {/* Content */}
                                {loadingRespostas ? (
                                    <div className="flex items-center justify-center py-20">
                                        <Iconify icon="mdi:loading" className="w-8 h-8 text-violet-500 animate-spin" />
                                    </div>
                                ) : respostas.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-[#ABABAD]">
                                        <Iconify icon="mdi:message-off-outline" className="w-12 h-12 mb-3" />
                                        <p className="text-sm font-medium">Nenhuma resposta encontrada</p>
                                        <p className="text-xs mt-1">As respostas aparecerão aqui quando alguém preencher o formulário</p>
                                    </div>
                                ) : respostasViewMode === 'planilha' ? (
                                    /* ======================== PLANILHA VIEW ======================== */
                                    <>
                                        {/* Filtros ativos indicator */}
                                        {hasActiveFilters && (
                                            <div className="px-5 py-2 bg-violet-50 dark:bg-violet-500/5 border-b border-violet-100 dark:border-violet-500/10 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Iconify icon="mdi:filter-outline" className="w-3.5 h-3.5 text-violet-600" />
                                                    <span className="text-xs text-violet-700 dark:text-violet-400 font-medium">
                                                        {respostasPagination.total} resposta{respostasPagination.total !== 1 ? 's' : ''} encontrada{respostasPagination.total !== 1 ? 's' : ''} · {activeFilterCount} filtro{activeFilterCount > 1 ? 's' : ''} ativo{activeFilterCount > 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => setPlanilhaFilters({})}
                                                    className="text-[10px] text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1 transition-colors"
                                                >
                                                    <Iconify icon="mdi:close-circle-outline" className="w-3 h-3" />
                                                    Limpar filtros
                                                </button>
                                            </div>
                                        )}
                                        <div className="overflow-auto flex-1 min-h-0">
                                            <table className="w-full text-left border-collapse min-w-[800px]">
                                                <thead>
                                                    <tr className="bg-gray-50 dark:bg-[#1A1D21] border-b border-gray-200 dark:border-[#383838]">
                                                        <th className="sticky left-0 z-10 bg-gray-50 dark:bg-[#1A1D21] px-4 py-3 text-[10px] font-bold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider border-r border-gray-200 dark:border-[#383838] min-w-[50px]">#</th>
                                                        {[
                                                            { key: '_respondente', label: 'Respondente', sticky: true, stickyLeft: '50px', minW: '160px', icon: null },
                                                            { key: '_status', label: 'Status', sticky: false, minW: '120px', icon: null },
                                                            { key: '_data', label: 'Data', sticky: false, minW: '140px', icon: null },
                                                            ...questions.map(q => ({
                                                                key: q.id,
                                                                label: q.title || 'Pergunta',
                                                                sticky: false,
                                                                minW: '180px',
                                                                maxW: '280px',
                                                                icon: QUESTION_TYPES.find(t => t.value === q.type)?.icon || 'mdi:help-circle-outline',
                                                                noFilter: q.type === 'file_upload' || q.type === 'info_block',
                                                            }))
                                                        ].map(col => {
                                                            const isFiltered = planilhaFilters[col.key] && planilhaFilters[col.key].size > 0;
                                                            const isOpen = planilhaFilterOpen === col.key;
                                                            return (
                                                                <th
                                                                    key={col.key}
                                                                    className={`px-4 py-3 border-r border-gray-100 dark:border-[#383838] relative ${col.sticky ? `sticky left-[${col.stickyLeft}] z-10 bg-gray-50 dark:bg-[#1A1D21] border-r-gray-200` : ''}`}
                                                                    style={{ minWidth: col.minW, maxWidth: col.maxW || 'none', ...(col.sticky ? { left: col.stickyLeft, zIndex: 10, background: 'inherit' } : {}) }}
                                                                >
                                                                    {col.noFilter ? (
                                                                        <div className="flex items-center gap-1.5 w-full text-left text-gray-500 dark:text-[#ABABAD]">
                                                                            {col.icon && <Iconify icon={col.icon} className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />}
                                                                            <span className="text-[10px] font-bold uppercase tracking-wider truncate">{col.label}</span>
                                                                        </div>
                                                                    ) : (
                                                                    <button
                                                                        type="button"
                                                                        className={`flex items-center gap-1.5 w-full text-left group transition-colors ${isFiltered ? 'text-violet-700 dark:text-violet-400' : 'text-gray-500 dark:text-[#ABABAD]'}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const opening = !isOpen;
                                                                            setPlanilhaFilterOpen(opening ? col.key : null);
                                                                            setPlanilhaFilterSearch('');
                                                                            if (opening) loadFilterOptions(col.key);
                                                                        }}
                                                                    >
                                                                        {col.icon && <Iconify icon={col.icon} className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />}
                                                                        <span className="text-[10px] font-bold uppercase tracking-wider truncate">{col.label}</span>
                                                                        <Iconify
                                                                            icon={isFiltered ? 'mdi:filter' : 'mdi:filter-outline'}
                                                                            className={`w-3 h-3 flex-shrink-0 transition-colors ${isFiltered ? 'text-violet-600' : 'text-gray-400 opacity-0 group-hover:opacity-100'}`}
                                                                        />
                                                                    </button>
                                                                    )}

                                                                    {/* Excel-style Filter Dropdown */}
                                                                    {!col.noFilter && isOpen && (() => {
                                                                        const allValues = planilhaFilterOptions[col.key] || [];
                                                                        const isLoadingOptions = loadingFilterOptions === col.key;
                                                                        const currentFilter = planilhaFilters[col.key];
                                                                        const isAllSelected = !currentFilter || currentFilter.size === 0;
                                                                        const searchedValues = planilhaFilterSearch
                                                                            ? allValues.filter(v => v.toLowerCase().includes(planilhaFilterSearch.toLowerCase()))
                                                                            : allValues;
                                                                        return (
                                                                            <div
                                                                                ref={planilhaFilterRef}
                                                                                className="absolute top-full left-0 mt-1 w-[220px] bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-lg shadow-xl z-50"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                {/* Search */}
                                                                                <div className="p-2 border-b border-gray-100 dark:border-[#383838]">
                                                                                    <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-[#1A1D21] rounded-md border border-gray-200 dark:border-[#383838]">
                                                                                        <Iconify icon="mdi:magnify" className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                                                        <input
                                                                                            type="text"
                                                                                            placeholder="Pesquisar..."
                                                                                            autoFocus
                                                                                            value={planilhaFilterSearch}
                                                                                            onChange={(e) => setPlanilhaFilterSearch(e.target.value)}
                                                                                            className="w-full text-[11px] bg-transparent outline-none text-gray-700 dark:text-[#D1D2D3] placeholder:text-gray-400"
                                                                                        />
                                                                                    </div>
                                                                                </div>

                                                                                {/* Select All / Clear */}
                                                                                <div className="px-2 py-1.5 border-b border-gray-100 dark:border-[#383838] flex items-center gap-2">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="text-[10px] font-medium text-violet-600 hover:text-violet-800 transition-colors"
                                                                                        onClick={() => setFilterAll(col.key, allValues, true)}
                                                                                    >
                                                                                        Selecionar tudo
                                                                                    </button>
                                                                                    <span className="text-gray-300">|</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="text-[10px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                                                                                        onClick={() => setFilterAll(col.key, [], false)}
                                                                                    >
                                                                                        Limpar
                                                                                    </button>
                                                                                </div>

                                                                                {/* Values list */}
                                                                                <div className="max-h-[200px] overflow-y-auto py-1">
                                                                                    {isLoadingOptions ? (
                                                                                        <div className="flex items-center justify-center py-4">
                                                                                            <Iconify icon="mdi:loading" className="w-4 h-4 text-violet-500 animate-spin" />
                                                                                        </div>
                                                                                    ) : searchedValues.length === 0 ? (
                                                                                        <p className="text-[11px] text-gray-400 text-center py-3">Nenhum resultado</p>
                                                                                    ) : searchedValues.map(val => {
                                                                                        const checked = isAllSelected || (currentFilter && currentFilter.has(val));
                                                                                        return (
                                                                                            <label
                                                                                                key={val}
                                                                                                className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-[#1A1D21] cursor-pointer transition-colors"
                                                                                            >
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={isAllSelected ? true : checked}
                                                                                                    onChange={() => {
                                                                                                        if (isAllSelected) {
                                                                                                            // First click from "all selected" → select only this one
                                                                                                            setPlanilhaFilters(prev => ({ ...prev, [col.key]: new Set([val]) }));
                                                                                                        } else {
                                                                                                            toggleFilterValue(col.key, val);
                                                                                                        }
                                                                                                    }}
                                                                                                    className="w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                                                                                                />
                                                                                                <span className="text-[11px] text-gray-700 dark:text-[#D1D2D3] truncate" title={val}>{val}</span>
                                                                                            </label>
                                                                                        );
                                                                                    })}
                                                                                </div>

                                                                                {/* Footer */}
                                                                                <div className="px-2 py-2 border-t border-gray-100 dark:border-[#383838] flex justify-end">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="px-3 py-1 text-[11px] font-medium bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors"
                                                                                        onClick={() => { setPlanilhaFilterOpen(null); setPlanilhaFilterSearch(''); }}
                                                                                    >
                                                                                        OK
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </th>
                                                            );
                                                        })}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredRespostas.map((resposta, rowIdx) => (
                                                        <tr
                                                            key={resposta._id}
                                                            className={`border-b border-gray-100 dark:border-[#383838] cursor-pointer transition-colors hover:bg-violet-50/30 dark:hover:bg-violet-500/5 ${rowIdx % 2 === 0 ? 'bg-white dark:bg-[#222529]' : 'bg-gray-50/30 dark:bg-[#1A1D21]/30'}`}
                                                            onClick={() => handleVerResposta(resposta._id)}
                                                        >
                                                            <td className="sticky left-0 z-10 px-4 py-3 text-xs text-gray-400 font-mono border-r border-gray-200" style={{ background: 'inherit' }}>
                                                                {(respostasPagination.page - 1) * respostasPagination.limit + rowIdx + 1}
                                                            </td>
                                                            <td className="sticky left-[50px] z-10 px-4 py-3 border-r border-gray-200" style={{ background: 'inherit' }}>
                                                                <p className="text-xs font-medium text-gray-700 dark:text-[#D1D2D3] truncate">{resposta.respondent?.name || 'Anônimo'}</p>
                                                                {resposta.respondent?.email && <p className="text-[10px] text-gray-400 dark:text-[#ABABAD] truncate">{resposta.respondent.email}</p>}
                                                            </td>
                                                            <td className="px-4 py-3 border-r border-gray-100">
                                                                <Badge className={`text-[10px] font-medium border ${statusConfig[resposta.status]?.className || 'bg-gray-100 text-gray-600'}`}>
                                                                    {statusConfig[resposta.status]?.label || resposta.status}
                                                                </Badge>
                                                            </td>
                                                            <td className="px-4 py-3 text-xs text-gray-500 border-r border-gray-100 whitespace-nowrap">
                                                                {formatDate(resposta.created_at)}
                                                            </td>
                                                            {questions.map((q) => {
                                                                const val = getAnswerForQuestion(resposta, q.id);
                                                                return (
                                                                    <td key={q.id} className="px-4 py-3 text-xs text-gray-700 border-r border-gray-100 max-w-[280px]">
                                                                        {val ? (
                                                                            <span className="line-clamp-2" title={val}>{val}</span>
                                                                        ) : (
                                                                            <span className="text-gray-300 italic">—</span>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Paginação */}
                                        {respostasPagination.pages > 1 && (
                                            <div className="px-5 py-3 border-t border-gray-100 dark:border-[#383838] flex items-center justify-between shrink-0">
                                                <p className="text-xs text-gray-500">
                                                    Mostrando {respostas.length} de {respostasPagination.total} respostas
                                                </p>
                                                <div className="flex items-center gap-1">
                                                    <Button variant="outline" size="sm" className="h-7 text-xs" disabled={respostasPagination.page <= 1} onClick={() => loadRespostas(respostasPagination.page - 1)}>
                                                        <Iconify icon="mdi:chevron-left" className="w-4 h-4" />
                                                    </Button>
                                                    <span className="text-xs text-gray-500 px-2">{respostasPagination.page} / {respostasPagination.pages}</span>
                                                    <Button variant="outline" size="sm" className="h-7 text-xs" disabled={respostasPagination.page >= respostasPagination.pages} onClick={() => loadRespostas(respostasPagination.page + 1)}>
                                                        <Iconify icon="mdi:chevron-right" className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    /* ======================== RESUMO VIEW ======================== */
                                    <>
                                        <div className="overflow-auto flex-1 min-h-0">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-gray-50/50 dark:bg-[#1A1D21]/50">
                                                    <TableHead className="pl-5 text-xs font-semibold text-gray-500">Respondente</TableHead>
                                                    <TableHead className="text-xs font-semibold text-gray-500">Status</TableHead>
                                                    <TableHead className="text-xs font-semibold text-gray-500">Respostas</TableHead>
                                                    <TableHead className="text-xs font-semibold text-gray-500">Duração</TableHead>
                                                    <TableHead className="text-xs font-semibold text-gray-500">Data</TableHead>
                                                    <TableHead className="text-xs font-semibold text-gray-500 pr-5 text-right">Ações</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {respostas.map((resposta) => (
                                                    <TableRow key={resposta._id} className="cursor-pointer hover:bg-violet-50/30 dark:hover:bg-violet-500/5" onClick={() => handleVerResposta(resposta._id)}>
                                                        <TableCell className="pl-5">
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-700 dark:text-[#D1D2D3]">
                                                                    {resposta.respondent?.name || 'Anônimo'}
                                                                </p>
                                                                {resposta.respondent?.email && (
                                                                    <p className="text-xs text-gray-400 dark:text-[#ABABAD]">{resposta.respondent.email}</p>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge className={`text-[10px] font-medium border ${statusConfig[resposta.status]?.className || 'bg-gray-100 text-gray-600'}`}>
                                                                {statusConfig[resposta.status]?.label || resposta.status}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-sm text-gray-600">
                                                            {resposta.answers?.length || 0} / {questions.length}
                                                        </TableCell>
                                                        <TableCell className="text-sm text-gray-600">
                                                            {formatDuration(resposta.metadata?.duration)}
                                                        </TableCell>
                                                        <TableCell className="text-sm text-gray-500">
                                                            {formatDate(resposta.created_at)}
                                                        </TableCell>
                                                        <TableCell className="pr-5 text-right">
                                                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleVerResposta(resposta._id); }}>
                                                                <Iconify icon="mdi:eye-outline" className="w-4 h-4 text-gray-400 hover:text-violet-600" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        </div>

                                        {/* Paginação */}
                                        {respostasPagination.pages > 1 && (
                                            <div className="px-5 py-3 border-t border-gray-100 dark:border-[#383838] flex items-center justify-between shrink-0">
                                                <p className="text-xs text-gray-500">
                                                    Mostrando {respostas.length} de {respostasPagination.total} respostas
                                                </p>
                                                <div className="flex items-center gap-1">
                                                    <Button variant="outline" size="sm" className="h-7 text-xs" disabled={respostasPagination.page <= 1} onClick={() => loadRespostas(respostasPagination.page - 1)}>
                                                        <Iconify icon="mdi:chevron-left" className="w-4 h-4" />
                                                    </Button>
                                                    <span className="text-xs text-gray-500 px-2">{respostasPagination.page} / {respostasPagination.pages}</span>
                                                    <Button variant="outline" size="sm" className="h-7 text-xs" disabled={respostasPagination.page >= respostasPagination.pages} onClick={() => loadRespostas(respostasPagination.page + 1)}>
                                                        <Iconify icon="mdi:chevron-right" className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {/* Sheet de Detalhe da Resposta */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent side="right" className="w-full sm:max-w-xl p-0 overflow-hidden flex flex-col bg-gradient-to-br from-slate-50 via-white to-violet-50/30 dark:from-[#1A1D21] dark:via-[#1A1D21] dark:to-[#1A1D21]">
                    {/* Header */}
                    <div className="px-6 py-5 border-b border-gray-100 dark:border-[#383838] bg-white/80 dark:bg-[#222529] backdrop-blur-md shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
                                <Iconify icon="mdi:message-reply-text" width={20} className="text-[#7C3AED]" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <SheetHeader className="p-0 space-y-0">
                                    <SheetTitle className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3] leading-tight">Detalhes da Resposta</SheetTitle>
                                    <SheetDescription className="text-[10px] text-gray-500 dark:text-[#ABABAD] font-medium truncate">
                                        {respostaDetalhe?.respondent?.name || 'Anônimo'}
                                        {respostaDetalhe?.respondent?.email ? ` · ${respostaDetalhe.respondent.email}` : ''}
                                    </SheetDescription>
                                </SheetHeader>
                            </div>
                            {respostaDetalhe && (
                                <Badge className={`text-[10px] font-medium border shrink-0 ${statusConfig[respostaDetalhe.status]?.className || 'bg-gray-100 text-gray-600'}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${respostaDetalhe.status === 'completed' ? 'bg-green-500' : respostaDetalhe.status === 'started' ? 'bg-blue-500' : 'bg-red-500'}`} />
                                    {statusConfig[respostaDetalhe.status]?.label || respostaDetalhe.status}
                                </Badge>
                            )}
                        </div>
                    </div>

                    {respostaDetalhe && (
                        <ScrollArea className="flex-1 overflow-y-auto">
                            <div className="px-6 py-5 space-y-5">

                                {/* Info Cards */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center shrink-0">
                                            <Iconify icon="mdi:account-outline" width={18} className="text-violet-600 dark:text-violet-400" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-semibold text-gray-900 dark:text-[#D1D2D3] truncate">{respostaDetalhe.respondent?.name || 'Anônimo'}</p>
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD]">Respondente</p>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center shrink-0">
                                            <Iconify icon="mdi:clock-outline" width={18} className="text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-gray-900 dark:text-[#D1D2D3]">{formatDuration(respostaDetalhe.metadata?.duration)}</p>
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD]">Duração</p>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center shrink-0">
                                            <Iconify icon="mdi:calendar-outline" width={18} className="text-amber-600 dark:text-amber-400" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-gray-900 dark:text-[#D1D2D3]">{formatDate(respostaDetalhe.created_at)}</p>
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD]">Data</p>
                                        </div>
                                    </div>
                                    {respostaDetalhe.metadata?.device ? (
                                        <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-green-50 dark:bg-green-500/10 flex items-center justify-center shrink-0">
                                                <Iconify icon="mdi:cellphone" width={18} className="text-green-600 dark:text-green-400" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-900 dark:text-[#D1D2D3]">{respostaDetalhe.metadata.device}</p>
                                                <p className="text-[10px] text-gray-500 dark:text-[#ABABAD]">Dispositivo</p>
                                            </div>
                                        </div>
                                    ) : respostaDetalhe.respondent?.phone ? (
                                        <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-green-50 dark:bg-green-500/10 flex items-center justify-center shrink-0">
                                                <Iconify icon="mdi:phone-outline" width={18} className="text-green-600 dark:text-green-400" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-900 dark:text-[#D1D2D3]">{respostaDetalhe.respondent.phone}</p>
                                                <p className="text-[10px] text-gray-500 dark:text-[#ABABAD]">Telefone</p>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>

                                {/* Contato extra (se já mostrou device acima e tem phone) */}
                                {respostaDetalhe.metadata?.device && respostaDetalhe.respondent?.phone && (
                                    <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-green-50 dark:bg-green-500/10 flex items-center justify-center shrink-0">
                                            <Iconify icon="mdi:phone-outline" width={18} className="text-green-600 dark:text-green-400" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-gray-900 dark:text-[#D1D2D3]">{respostaDetalhe.respondent.phone}</p>
                                            <p className="text-[10px] text-gray-500 dark:text-[#ABABAD]">Telefone</p>
                                        </div>
                                    </div>
                                )}

                                {/* Respostas */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider">Respostas</h4>
                                        <span className="text-[10px] text-gray-400 dark:text-[#ABABAD] font-medium bg-gray-100 dark:bg-[#383838] px-1.5 py-0.5 rounded-full">
                                            {respostaDetalhe.answers?.length || 0}/{questions.length}
                                        </span>
                                    </div>

                                    {respostaDetalhe.answers?.map((answer, index) => {
                                        const qType = QUESTION_TYPES.find(t => t.value === answer.question_type);
                                        const hasValue = answer.value && (Array.isArray(answer.value) ? answer.value.length > 0 : true);

                                        return (
                                            <div key={index} className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] overflow-hidden transition-all hover:shadow-sm">
                                                {/* Pergunta header */}
                                                <div className="px-4 py-3 flex items-start gap-3">
                                                    <div className="w-7 h-7 rounded-lg bg-violet-100 dark:bg-violet-500/10 flex items-center justify-center shrink-0 mt-0.5">
                                                        <Iconify icon={qType?.icon || 'mdi:help-circle-outline'} width={14} className="text-violet-600 dark:text-violet-400" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-semibold text-gray-800 dark:text-[#D1D2D3] leading-snug">{answer.question_title || 'Pergunta'}</p>
                                                        <p className="text-[10px] text-gray-400 dark:text-[#ABABAD] mt-0.5">{qType?.label || answer.question_type}</p>
                                                    </div>
                                                    <span className="text-[10px] text-gray-400 dark:text-[#ABABAD] font-mono shrink-0 mt-1">{index + 1}/{respostaDetalhe.answers.length}</span>
                                                </div>

                                                {/* Resposta valor */}
                                                <div className="px-4 pb-3">
                                                    <div className={`rounded-lg px-3.5 py-2.5 ${hasValue ? 'bg-violet-50/70 dark:bg-violet-500/5 border border-violet-100/50 dark:border-violet-500/10' : 'bg-gray-50 dark:bg-[#1A1D21] border border-gray-100 dark:border-[#383838]'}`}>
                                                        {answer.question_type === 'file_upload' && Array.isArray(answer.value) && answer.value.length > 0 && typeof answer.value[0] === 'object' && answer.value[0].url ? (
                                                            <div className="space-y-1.5">
                                                                {answer.value.map((file, fi) => (
                                                                    <a
                                                                        key={fi}
                                                                        href={file.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="flex items-center gap-2.5 text-xs text-violet-700 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300 transition-colors group"
                                                                    >
                                                                        <div className="w-6 h-6 rounded-md bg-violet-100 dark:bg-violet-500/10 flex items-center justify-center shrink-0 group-hover:bg-violet-200 dark:group-hover:bg-violet-500/20 transition-colors">
                                                                            <Iconify icon={file.mimetype?.startsWith('image/') ? 'mdi:file-image-outline' : file.mimetype === 'application/pdf' ? 'mdi:file-pdf-box' : 'mdi:file-outline'} width={13} />
                                                                        </div>
                                                                        <span className="font-medium truncate">{file.filename || 'Arquivo'}</span>
                                                                        {file.size ? <span className="text-[10px] text-gray-400 dark:text-[#ABABAD] shrink-0">({(file.size / 1024).toFixed(0)} KB)</span> : null}
                                                                        <Iconify icon="mdi:open-in-new" width={12} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        ) : answer.question_type === 'info_block' && Array.isArray(answer.value) && answer.value.length > 0 && typeof answer.value[0] === 'object' ? (
                                                            <div className="space-y-2">
                                                                {answer.value.map((block, bi) => {
                                                                    const question = questions.find(q => q.id === answer.question_id);
                                                                    const fieldMap = {};
                                                                    (question?.blockFields || []).forEach(f => { fieldMap[f.id] = f.label || f.id; });
                                                                    return (
                                                                        <div key={bi} className="space-y-1">
                                                                            {answer.value.length > 1 && (
                                                                                <p className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider">{question?.title || 'Bloco'} #{bi + 1}</p>
                                                                            )}
                                                                            {Object.entries(block).map(([k, v]) => (
                                                                                <div key={k} className="flex items-baseline gap-1.5">
                                                                                    <span className="text-[10px] font-medium text-gray-500 dark:text-[#ABABAD] shrink-0">{fieldMap[k] || k}:</span>
                                                                                    <span className="text-xs text-gray-800 dark:text-[#D1D2D3]">{v}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : hasValue ? (
                                                            <p className="text-xs text-gray-800 dark:text-[#D1D2D3] leading-relaxed">
                                                                {Array.isArray(answer.value) ? answer.value.join(', ') : (typeof answer.value === 'object' ? JSON.stringify(answer.value) : answer.value)}
                                                            </p>
                                                        ) : (
                                                            <p className="text-xs text-gray-400 dark:text-[#ABABAD] italic">Sem resposta</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {(!respostaDetalhe.answers || respostaDetalhe.answers.length === 0) && (
                                        <div className="flex flex-col items-center justify-center py-10 text-center">
                                            <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-[#383838] flex items-center justify-center mb-3">
                                                <Iconify icon="mdi:message-off-outline" width={24} className="text-gray-400 dark:text-[#ABABAD]" />
                                            </div>
                                            <p className="text-xs font-medium text-gray-500 dark:text-[#ABABAD]">Nenhuma resposta registrada</p>
                                        </div>
                                    )}
                                </div>

                            </div>
                        </ScrollArea>
                    )}
                </SheetContent>
            </Sheet>

            {/* Preview Modal */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-none w-screen h-screen p-0 border-0 bg-transparent shadow-none shadow-transparent outline-none ring-0">
                    <style>{previewCSS}</style>

                    {isStepByStep ? (
                        // Google Forms — Step by Step
                        <div className="fixed inset-0 z-50 flex flex-col font-sans" style={{ background: pvPageBg }}>
                            {/* Progress Bar */}
                            <div className="w-full bg-[#e0e0e0]" style={{ height: 4 }}>
                                <div className="h-full transition-all duration-500 ease-out" style={{ width: `${((previewStep + 1) / totalPreviewSteps) * 100}%`, background: pvAccent }} />
                            </div>

                            {/* Top bar */}
                            <div className="px-6 py-3 flex items-center justify-between border-b border-[#dadce0] bg-white">
                                <div className="flex items-center gap-3 min-w-0">
                                    {settings.logoUrl && <img src={settings.logoUrl} alt="Logo" style={{ height: 28, objectFit: 'contain' }} />}
                                    <p className="text-sm font-medium text-[#202124] truncate">{title}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-[#5f6368]">{previewStep + 1} de {totalPreviewSteps}</span>
                                    <button onClick={() => setPreviewOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                                        <Iconify icon="mdi:close" className="w-4 h-4 text-[#5f6368]" />
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto">
                                <div className="max-w-2xl mx-auto px-4 py-8 md:py-12">
                                    <div key={previewStep} className="animate-in fade-in duration-300">

                                        {isOnIdentificationStep ? (
                                            <div className="gfp-header">
                                                <div className="h-[10px]" style={{ background: pvAccent }} />
                                                <div className="p-6">
                                                    {settings.logoUrl && (
                                                        <div className="flex justify-center mb-4">
                                                            <img src={settings.logoUrl} alt="Logo" className="max-h-20 max-w-[200px] object-contain" />
                                                        </div>
                                                    )}
                                                    <h2 className="text-xl font-normal text-[#202124] mb-2">Identificação</h2>
                                                    <p className="text-sm text-[#5f6368] mb-6">Preencha seus dados para continuar.</p>
                                                    <div className="space-y-5">
                                                        {enabledIdentificationFields.map(field => (
                                                            <div key={field.key}>
                                                                <label className="block text-sm text-[#202124] mb-2">
                                                                    {field.label}
                                                                    {field.required && <span className="text-[#d93025] ml-1">*</span>}
                                                                </label>
                                                                <input type={field.type} placeholder={field.placeholder} className="gfp-input" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="mt-8">
                                                        <button className="gfp-btn" onClick={() => setPreviewStep(1)}>
                                                            Avançar
                                                            <Iconify icon="mdi:arrow-right" className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                        <>
                                            {(() => {
                                                const q = questions[currentQuestionIndex];
                                                if (!q) return null;
                                                return (
                                                    <div className="gfp-card">
                                                        <h2 className="text-base text-[#202124] mb-1">
                                                            {q.title || 'Pergunta sem título'}
                                                            {q.required && <span className="text-[#d93025] ml-1">*</span>}
                                                        </h2>
                                                        {q.description && <p className="text-xs text-[#5f6368] mb-3">{q.description}</p>}
                                                        <div className="mt-4">
                                                            {renderPreviewField(q)}
                                                        </div>
                                                        <div className="mt-8 flex items-center gap-3">
                                                            <button
                                                                className="gfp-btn"
                                                                onClick={() => {
                                                                    if (previewStep < totalPreviewSteps - 1) {
                                                                        setPreviewStep(prev => prev + 1);
                                                                    } else {
                                                                        setPreviewOpen(false);
                                                                    }
                                                                }}
                                                            >
                                                                {previewStep === totalPreviewSteps - 1 ? 'Enviar' : 'Avançar'}
                                                                <Iconify icon={previewStep === totalPreviewSteps - 1 ? 'mdi:check' : 'mdi:arrow-right'} className="w-4 h-4" />
                                                            </button>
                                                            {previewStep > 0 && (
                                                                <button className="gfp-btn-sec" onClick={() => setPreviewStep(prev => prev - 1)}>
                                                                    Voltar
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </>
                                        )}

                                    </div>
                                </div>
                            </div>

                            {/* Keyboard hint */}
                            <div className="hidden md:flex items-center gap-2 px-6 py-3 border-t border-[#dadce0] bg-white text-xs text-[#80868b]">
                                <kbd className="px-1.5 py-0.5 bg-[#f1f3f4] border border-[#dadce0] rounded text-[10px] font-medium">Enter ↵</kbd>
                                <span>para avançar</span>
                            </div>
                        </div>
                    ) : (
                        // Google Forms — Normal (List) mode
                        <div className="fixed inset-0 z-50 flex flex-col font-sans" style={{ background: pvPageBg }}>
                            {/* Close button */}
                            <button
                                className="absolute top-4 right-4 z-50 w-9 h-9 flex items-center justify-center rounded-full bg-white border border-[#dadce0] hover:bg-gray-50 transition-colors shadow-sm"
                                onClick={() => setPreviewOpen(false)}
                            >
                                <Iconify icon="mdi:close" className="w-4 h-4 text-[#5f6368]" />
                            </button>

                            <div className="h-full overflow-y-auto">
                                <div className="max-w-2xl mx-auto px-4 py-8 md:py-12">

                                    {/* Header Card */}
                                    <div className="gfp-header">
                                        <div className="h-[10px] rounded-t-lg" style={{ background: pvAccent }} />
                                        <div className="p-6">
                                            {settings.logoUrl && (
                                                <div className="flex justify-center mb-4">
                                                    <img src={settings.logoUrl} alt="Logo" className="max-h-20 max-w-[200px] object-contain" />
                                                </div>
                                            )}
                                            <h1 className="text-2xl md:text-[28px] font-normal text-[#202124] leading-snug">
                                                {title || 'Sem Título'}
                                            </h1>
                                            {description && (
                                                <p className="text-sm text-[#202124] mt-3 leading-relaxed whitespace-pre-line">{description}</p>
                                            )}
                                            {!settings.allowMultipleResponses && (
                                                <div className="mt-4 flex items-center gap-1.5 text-xs text-[#5f6368]">
                                                    <Iconify icon="mdi:lock-outline" className="w-3.5 h-3.5" />
                                                    <span>Este formulário aceita apenas uma resposta</span>
                                                </div>
                                            )}
                                            {questions.some(q => q.required) && (
                                                <>
                                                    <hr className="border-[#dadce0] mt-4 mb-3" />
                                                    <p className="text-xs text-[#d93025]">* Indica uma pergunta obrigatória</p>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Identification Card */}
                                    {hasIdentificationStep && (
                                        <div className="gfp-card">
                                            <div className="flex items-center gap-2 mb-4">
                                                <Iconify icon="mdi:account-circle-outline" className="w-5 h-5" style={{ color: pvAccent }} />
                                                <h2 className="text-base font-medium text-[#202124]">Identificação</h2>
                                            </div>
                                            <div className="space-y-5">
                                                {enabledIdentificationFields.map(field => (
                                                    <div key={field.key}>
                                                        <label className="block text-sm text-[#202124] mb-2">
                                                            {field.label}
                                                            {field.required && <span className="text-[#d93025] ml-1">*</span>}
                                                        </label>
                                                        <input type={field.type} placeholder={field.placeholder} className="gfp-input" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Question Cards */}
                                    {questions.map((q, index) => (
                                        <div key={q.id} className="gfp-card">
                                            <h2 className="text-base text-[#202124] mb-1">
                                                {q.title || 'Pergunta sem título'}
                                                {q.required && <span className="text-[#d93025] ml-1">*</span>}
                                            </h2>
                                            {q.description && <p className="text-xs text-[#5f6368] mb-3">{q.description}</p>}
                                            <div className="mt-4">
                                                {renderPreviewField(q)}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Submit Section */}
                                    <div className="flex items-center justify-between mt-2 mb-6">
                                        <button className="gfp-btn">Enviar</button>
                                        <button className="gfp-link">Limpar formulário</button>
                                    </div>

                                    {/* Footer */}
                                    <div className="text-center pb-8 space-y-2">
                                        <p className="text-xs text-[#5f6368]">Nunca envie informações sensíveis por formulários.</p>
                                        <p className="text-xs text-[#80868b]">Suas respostas são confidenciais e protegidas.</p>
                                    </div>

                                </div>
                            </div>
                        </div>
                    )}

                    {/* Floating Color Picker */}
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2.5 bg-black/50 backdrop-blur-xl px-4 py-3 rounded-2xl shadow-2xl border border-white/10">
                        {/* Solid Colors */}
                        <Iconify icon="mdi:palette-outline" className="w-4 h-4 text-white/50 flex-shrink-0" />
                        <div className="flex items-center gap-1.5">
                            {[
                                { color: '#7C3AED', label: 'Violeta' },
                                { color: '#2563EB', label: 'Azul' },
                                { color: '#059669', label: 'Verde' },
                                { color: '#D97706', label: 'Âmbar' },
                                { color: '#DC2626', label: 'Vermelho' },
                                { color: '#DB2777', label: 'Rosa' },
                                { color: '#0F172A', label: 'Escuro' },
                                { color: '#94A3B8', label: 'Cinza' },
                            ].map(preset => (
                                <button
                                    key={preset.color}
                                    type="button"
                                    title={preset.label}
                                    onClick={() => {
                                        setSettings(prev => ({ ...prev, backgroundColor: preset.color }));
                                        markAsChanged();
                                    }}
                                    className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-125 flex-shrink-0 ${
                                        settings.backgroundColor === preset.color
                                            ? 'border-white scale-110 shadow-lg shadow-white/20'
                                            : 'border-white/20 hover:border-white/50'
                                    }`}
                                    style={{ backgroundColor: preset.color }}
                                />
                            ))}
                        </div>
                        <div className="w-px h-5 bg-white/20 flex-shrink-0" />
                        {/* Gradients */}
                        <Iconify icon="mdi:gradient-horizontal" className="w-4 h-4 text-white/50 flex-shrink-0" />
                        <div className="flex items-center gap-1.5">
                            {[
                                { value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', label: 'Púrpura' },
                                { value: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', label: 'Esmeralda' },
                                { value: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', label: 'Pôr do Sol' },
                                { value: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', label: 'Lavanda' },
                                { value: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)', label: 'Oceano' },
                                { value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', label: 'Flamingo' },
                                { value: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)', label: 'Noturno' },
                                { value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', label: 'Pêssego' },
                            ].map(preset => (
                                <button
                                    key={preset.label}
                                    type="button"
                                    title={preset.label}
                                    onClick={() => {
                                        setSettings(prev => ({ ...prev, backgroundColor: preset.value }));
                                        markAsChanged();
                                    }}
                                    className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-125 flex-shrink-0 ${
                                        settings.backgroundColor === preset.value
                                            ? 'border-white scale-110 shadow-lg shadow-white/20'
                                            : 'border-white/20 hover:border-white/50'
                                    }`}
                                    style={{ background: preset.value }}
                                />
                            ))}
                        </div>
                        <div className="w-px h-5 bg-white/20 flex-shrink-0" />
                        {/* Custom Color Picker */}
                        <div className="relative flex-shrink-0">
                            <input
                                type="color"
                                value={(() => { const bg = settings.backgroundColor || '#7C3AED'; return bg.startsWith('#') ? bg : '#7C3AED'; })()}
                                onChange={(e) => {
                                    setSettings(prev => ({ ...prev, backgroundColor: e.target.value }));
                                    markAsChanged();
                                }}
                                className="w-6 h-6 rounded-full cursor-pointer border-2 border-white/20 hover:border-white/50 transition-all p-0 bg-transparent"
                                title="Cor personalizada"
                            />
                        </div>
                    </div>

                </DialogContent>
            </Dialog>

        </div>
    );
}
